import {
  enginePricingEvidence,
  rememberEnginePricingEvidence,
  type EnginePricingEvidence,
} from '../payroll/engine-pricing-evidence';
/* eslint-disable statura/no-floating-point-money --
 * Canonical Nurses Award MA000034 regulatory calculator validated against
 * FWC pay guide (1 Oct 2025 + quarterly indexation). Every $-arithmetic
 * line in this file is part of the regulated calc. The cents-scaled
 * follow-up tracked in floating-point-money-triage applies to all award
 * engines together; scoping per-line eslint-disable here would be 26
 * comments without aiding readability.
 */

/**
 * Phase 5 §7 — Nurses Award 2020 (MA000034) engine.
 *
 * Implements the per-shift pay calculator for nurses (RN, EN, NP) under
 * MA000034. Reads all rate / penalty / allowance values from the
 * `RateContext` it is passed (Phase 1 effective-dated rate version).
 * No hardcoded constants for rates — every numeric value comes from
 * `award_rate_versions` + child line tables seeded by the companion
 * migration (`20260603100000_seed_nurses_v1.sql`).
 *
 * IMPORTANT ARCHITECTURAL DIFFERENCES from the SCHADS engine:
 *
 * 1. **Casual loading is PER-CLAUSE in MA000034 — narrowly ADDITIVE for
 *    cl 20 shiftwork loadings; MULTIPLICATIVE for cl 19 OT, cl 21
 *    weekend penalties, cl 28 PH ordinary.** Pre-Phase-1J the engine
 *    applied ADDITIVE globally based on a misreading of cl 11.4 as a
 *    global rule. Phase 1.4 Cluster A primary-source verification (see
 *    `docs/operations/clause-verifications/2026-05-16-aged-care-legislation-nurses-residual-clause-sweep.md`)
 *    established cl 11.4 is narrowly scoped to cl 20 shiftwork loadings;
 *    cl 19.2 OT and cl 21.1/21.2 weekend penalties + cl 28.2(a) PH
 *    ordinary all quote rates as "X% of the **casual hourly rate**" —
 *    multiplicative on the casual hourly rate, which already embeds the
 *    25% loading via cl 11.1.
 *
 *    Per-clause table:
 *      cl 11.1 ordinary hours:  ADDITIVE (1.0 + 0.25 = 1.0 × 1.25; same answer)
 *      cl 20.2(a) afternoon:    ADDITIVE per cl 11.4 (+12.5% delta on top of weekday)
 *      cl 20.2(b) night:        ADDITIVE per cl 11.4 (+15% delta on top of weekday)
 *      cl 21.1 Saturday:        MULTIPLICATIVE (1.5 × casualMult)
 *      cl 21.2 Sunday:          MULTIPLICATIVE (1.75 × casualMult)
 *      cl 28.2(a) PH ordinary:  MULTIPLICATIVE (phMult-from-ctx × casualMult)
 *      cl 19.1(a)/19.2(a) OT:   MULTIPLICATIVE (tier1/tier2/sun/ph × casualMult)
 *
 *    Pre-Phase-1J the engine under-paid every casual nurse on every
 *    weekend or OT shift by 0.125–0.375× of base per hour. PR #18 still
 *    draft so zero production exposure; fix landed in commit shipping
 *    Nurses 5.5.5 → 5.5.6. See errata catalogue entry +
 *    `project_nurses_casual_loading_model.md` memory.
 *
 * 2. **Substitution rule** (cl 19.1(c)): "Overtime rates are in
 *    substitution for and not cumulative upon the shiftwork loadings
 *    and weekend penalty rates." If a shift triggers BOTH overtime AND
 *    a Sat/Sun/PH penalty, you pay the overtime rate, NOT penalty +
 *    overtime stacked. The engine takes max(overtime, penalty), not the
 *    sum.
 *
 * 3. **RN Level 4 + Level 5 exclusions** (cl 19.1(b), 19.2(b), 21,
 *    20.2(e)): excluded from overtime, weekend penalties, shift
 *    loadings, and cl 17 allowances. Engine short-circuits these
 *    classifications.
 *
 * 4. **Different overtime thresholds**: 8h/shift OR 38h/week (SCHADS
 *    is 10h/shift). First 2h Mon–Sat at 1.5×, then 2.0×. Sunday all
 *    OT at 2.0×. PH all OT at 2.5× (no first-2h cutover).
 *
 * 5. **Two classification streams**:
 *    - `nurse` (cl 15.1) — non-aged-care rates
 *    - `aged_care_nurse` (cl 15.3) — aged-care branch with higher rates
 *      (post-PR791118 work-value increases, 1 Oct 2025)
 *    The worker's classification record selects the stream.
 *
 * 6. **REMOVED Phase 1J (Award Engine Integrity 2026-05-16)** — the
 *    "permanent night shift worker +30% loading" code path was a
 *    phantom; cl 20.2(c) is the noon-to-6pm non-application carveout,
 *    NOT a +30% loading. Exhaustive grep of consolidated MA000034
 *    (23 Jan 2026) returned zero hits for any +30% / permanent-night /
 *    rostered-consecutive loading. Likely cross-award contamination
 *    from Aged Care Award MA000018 cl 22.3. The `permanentNightShiftWorker`
 *    input field was removed; weekday night shifts now pay the single
 *    cl 20.2(b) +15% loading regardless of rostered cadence. See errata
 *    catalogue + Phase 1J fix-shape verification artifact.
 *
 * Known v1 limitations (flagged by aged-care-legislation review,
 * scheduled for follow-up before broad rollout):
 *
 * - **Afternoon / night shift detection is per-segment, not whole-shift.**
 *   Cl 20.2(a)(b) define afternoon and night shifts by the *commencement
 *   and finish times of the shift as a whole*. The engine currently
 *   classifies each 15-min segment by its own start hour, which produces
 *   correct loading dollars for shifts that start AND end inside a single
 *   band but can over-pay (eg a 06:00–14:00 dayshift gets afternoon
 *   loading on 12:00–14:00) or mis-classify shifts whose character
 *   straddles a boundary. Refactor to a whole-shift classifier in a
 *   follow-up commit.
 *
 * - **OT-end-of-shift assumption.** The substitution-rule logic in
 *   `calculateWithOvertime` assumes the overtime hours are at the end
 *   of the shift, in the day type of the shift's end. Pathological
 *   cases (shift spanning Sat→Sun midnight where OT is in Sun, or
 *   end-of-shift in afternoon vs night band) are not handled exactly.
 *   Industry practice treats end-of-shift as OT, so this is a
 *   defensible v1 approximation.
 */

import {
  coverageGapsField,
  findPenalty as findPenaltyLine,
  isRequiredAllowanceMissing,
  pushPenaltyCoverageGap,
  pushRateRowCoverageGap,
  resolveRequiredAllowance,
  resolveRequiredPenalty,
  selectRateLineByEmployment,
  EBA_REQUIRED_PENALTY_COVERAGE_GAP_REASON,
  COVERAGE_GAP_MULTIPLIER,
  type RateContext,
  type RateLine,
  type PenaltyLine,
  type AllowanceLine,
} from '../award-rate-lookup';
import { dateOnlyInTz } from '../timezone';
import { backOutRoundingUnits, roundMoney, sumMoney, moneyToCents } from '../money-rounding';
import type {
  ShiftPayBreakdown,
  ShiftPayInput,
  ShiftPayWithOvertimeInput,
  ShiftPayAllowanceLine,
  ShiftClassificationSnapshot,
  NormalizedSchadsLevel,
} from '../schads';
import { payrollBreakMinutesForEngine } from '../payroll/break-treatment';
import { applyDstStartOrdinaryTopUp } from '../payroll/multi-zone-pay-clock';
import {
  overtimeMealAllowanceCrossings,
  shiftClassificationGeometry,
} from '../payroll/shift-classification-geometry';
import {
  ordinaryHoursForShiftWithinConfiguredLimits,
  subtractOvertimeFromOrdinaryBands,
  ordinaryOvertimeBackoutHours,
} from '../schads';
import {
  calculateEbaTieredOvertimePay,
  inferShiftBandFromConditionalPenaltyCode,
  otExportTierHours,
  overtimeOnLoadedRateEnabledFromContext,
  primaryOvertimeRuleFromContext,
  resolveShiftLoadingPremiumFromContext,
} from '../payroll/eba-cert-reducer';
import { localTimeHHMM } from '../payroll/eba-conditional-penalties';
import { medeaFortnightCapOvertimeSplit } from '../payroll/award-part-time-overtime';
import {
  classifyShift,
  getOvertimeSegmentBreakdown,
  getOvertimeShiftLoadingBandBreakdown,
  pickOvertimeShiftLoadingBand,
  type DayType,
} from './shift-classifier';
import { retainShiftLoadingOnOvertimeForProfile } from '../payroll/eba-block-compiler';
import { calculateRecallPay, type RecallPayBreakdown, type RecallPayInput } from './recall';
import type { AwardEngine, AwardCode } from './types';
import {
  buildNativeEvaluationTrace,
  unavailableEvaluationTrace,
  type EvaluationTrace,
  type TracePrice,
} from './evaluation-trace';
import {
  NURSES_SHIFT_RULES,
  NURSES_RECALL_RULES,
  NURSES_MEAL_OT_EMISSION,
  NURSES_STREAMS,
  NURSES_PH_OT_MULT,
  NURSES_LEAVE,
} from '../award-data/nurses';
import {
  conditionalPenaltyBlockCodeForKind,
  findConditionalPenaltyLine,
  pushMissingConditionalPenaltyGap,
} from '../payroll/eba-conditional-penalties';

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

const round2 = (n: number): number => roundMoney(n);
const round4 = (n: number): number => Math.round(n * 10000) / 10000;

/**
 * `getDay` retained for weekday-only shift-loading substitution guard in
 * `calculateWithOvertime`. Phase 1A.3 removed the engine-local segment
 * classifier in favour of `./shift-classifier.ts`.
 */
function getDay(date: Date, tz: string): number {
  const day = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(date);
  return { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[
    day as 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'
  ];
}

// ═══════════════════════════════════════════════════════════════════════════
// Classification + rate resolution
// ═══════════════════════════════════════════════════════════════════════════

interface ResolvedNurseRate {
  hourlyRate: number;
  level: string;
  payPoint: string | null;
  /** Cl 19.1(b), 19.2(b), 21, 20.2(e) — RN L4 + L5 are excluded from
   * overtime, weekend penalties, shift loadings, and cl 17 allowances. */
  excludedFromPenalties: boolean;
  /** True iff input.schadsLevel could be parsed into a known classification. */
  recognised: boolean;
  includesCasualLoading?: boolean;
}

/**
 * Parse the `schadsLevel` field into a Nurses classification. Examples:
 *  - 'RN_L1.1' → { level: 'RN_L1', payPoint: '1' }
 *  - 'RN_L3'   → { level: 'RN_L3', payPoint: null }
 *  - 'EN_L4'   → { level: 'EN_L4', payPoint: null }
 *  - 'NP_1'    → { level: 'NP', payPoint: '1' }
 */
function parseClassification(raw: string): { level: string; payPoint: string | null } | null {
  const trimmed = raw.trim().toUpperCase();
  if (!trimmed) return null;

  // RN_L1.1 / EN_L3.2 — level + pay point with dot separator
  const dotMatch = trimmed.match(/^(RN_L\d+|EN_L\d+|NP)\.(\d+)$/);
  if (dotMatch) {
    return { level: dotMatch[1]!, payPoint: dotMatch[2]! };
  }

  // RN_L1_PP1 / EN_L2_PP3 — explicit pay point suffix
  const ppMatch = trimmed.match(/^(RN_L\d+|EN_L\d+|NP)_PP(\d+)$/);
  if (ppMatch) {
    return { level: ppMatch[1]!, payPoint: ppMatch[2]! };
  }

  // NP_1 / NP_2 — NP year (single classification)
  const npMatch = trimmed.match(/^NP_(\d+)$/);
  if (npMatch) {
    return { level: 'NP', payPoint: npMatch[1]! };
  }

  // RN_L4 / RN_L5 / EN_L1 — level only
  const levelMatch = trimmed.match(/^(RN_L\d+|EN_L\d+|NP)$/);
  if (levelMatch) {
    return { level: levelMatch[1]!, payPoint: null };
  }

  return null;
}

function isExcludedClassification(level: string): boolean {
  // Cl 19.1(b), 19.2(b), 21, 20.2(e) — RN L4/L5 are excluded from
  // overtime, weekend penalties, shift loadings, cl 17 allowances.
  // Aged-care branch (cl 15.3) keeps the _AC suffix on level codes.
  return level === 'RN_L4' || level === 'RN_L5' || level === 'RN_L4_AC' || level === 'RN_L5_AC';
}

function lookupRate(
  ctx: RateContext,
  stream: 'nurse' | 'aged_care_nurse',
  level: string,
  payPoint: string | null,
  employment: EmploymentLabel,
  coverageGaps?: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): RateLine | null {
  const candidates = ctx.rates.filter(
    (r) =>
      r.classificationStream === stream &&
      r.classificationLevel === level &&
      (r.payPoint ?? null) === payPoint,
  );
  if (candidates.length === 0) return null;

  const { rate, ambiguous } = selectRateLineByEmployment(candidates, employment);
  if (ambiguous && coverageGaps) {
    pushRateRowCoverageGap(coverageGaps, stream, level, payPoint, employment);
    return null;
  }
  return rate;
}

/** Excluded RN L4/L5 classifications receive their flat hourly rate, including
 * any casual loading already in it. Other nurse paths apply casual loading in
 * their ordinary, penalty and overtime calculations, so use the unloaded base. */
function nurseBaseHourlyRate(
  rate: RateLine,
  employment: EmploymentLabel,
  excludedFromPenalties: boolean,
): number {
  if (employment !== 'CASUAL' || rate.includesCasualLoading !== true) return rate.hourlyRate;
  if (excludedFromPenalties) return rate.hourlyRate;
  // Exact inverse 4/5 ratio; round only the final engine money components.
  return (rate.hourlyRate * 4) / 5;
}

/**
 * Resolve hourly rate from ctx + the shift input.
 *
 * Lookup precedence:
 *   1. Explicit `classificationLevel` + `classificationPayPoint` on the
 *      input (preferred — supports the full Nurses classification matrix
 *      including `_AC` aged-care suffixes that the parser doesn't match).
 *   2. Parse `schadsLevel` string (legacy path for callers that haven't
 *      been updated; only matches the cl 15.1 RN/EN/NP shapes).
 *
 * Returns `{ hourlyRate: 0, recognised: false }` when no rate line matches
 * — the caller surfaces this as a warning (mirrors SCHADS behaviour).
 */
function resolveNurseRate(
  ctx: RateContext,
  input: ShiftPayInput & NursesShiftInputExtras,
  stream: 'nurse' | 'aged_care_nurse',
  employment: EmploymentLabel,
  coverageGaps?: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): ResolvedNurseRate {
  // Path 1: explicit classification fields
  if (input.classificationLevel) {
    const level = input.classificationLevel;
    const payPoint = input.classificationPayPoint ?? null;
    const rate = lookupRate(ctx, stream, level, payPoint, employment, coverageGaps);
    if (!rate) {
      return {
        hourlyRate: 0,
        level,
        payPoint,
        excludedFromPenalties: isExcludedClassification(level),
        recognised: false,
      };
    }
    return {
      hourlyRate: nurseBaseHourlyRate(rate, employment, isExcludedClassification(level)),
      level,
      payPoint,
      excludedFromPenalties: isExcludedClassification(level),
      recognised: true,
      includesCasualLoading: false,
    };
  }

  // Path 2: legacy string parsing
  const parsed = parseClassification(input.schadsLevel ?? '');
  if (!parsed) {
    return {
      hourlyRate: 0,
      level: input.schadsLevel ?? '',
      payPoint: null,
      excludedFromPenalties: false,
      recognised: false,
    };
  }

  const rate = lookupRate(ctx, stream, parsed.level, parsed.payPoint, employment, coverageGaps);
  if (!rate) {
    return {
      hourlyRate: 0,
      level: parsed.level,
      payPoint: parsed.payPoint,
      excludedFromPenalties: isExcludedClassification(parsed.level),
      recognised: false,
    };
  }

  return {
    hourlyRate: nurseBaseHourlyRate(rate, employment, isExcludedClassification(parsed.level)),
    level: parsed.level,
    payPoint: parsed.payPoint,
    excludedFromPenalties: isExcludedClassification(parsed.level),
    recognised: true,
    includesCasualLoading: false,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Penalty + allowance lookups
// ═══════════════════════════════════════════════════════════════════════════

type EmploymentLabel = 'PERMANENT' | 'CASUAL';

const CASUAL_LOADING_NURSES = 0.25;

// Phase 1J PH OT semantics: NURSES_PH_OT_MULT (imported from award-data/nurses)
// is hardcoded to 2.5 per cl 19.1(a)(iii) / cl 19.2(a)(iii). DB seeded the
// PUBLIC_HOLIDAY penalty band at 2.00 (cl 28.2(a) ORDINARY). Engine reads
// NURSES_PH_OT_MULT directly for the OT path to keep ordinary vs OT distinct,
// independent of seed. Casual PH OT = 2.5 × 1.25 = 3.125 × base (multiplicative
// per cl 19.2(a)(iii) "250% of the casual hourly rate").

function findPenalty(
  penalties: PenaltyLine[],
  band: string,
  employment: EmploymentLabel,
): number | null {
  // Stream-specific first; fall back to stream-agnostic rule.
  const specific = penalties.find(
    (p) => p.band === band && p.employmentType === employment && p.classificationStream != null,
  );
  if (specific) return specific.multiplier;
  const generic = penalties.find(
    (p) => p.band === band && p.employmentType === employment && p.classificationStream == null,
  );
  return generic?.multiplier ?? null;
}

function ebaPermanentNightMultiplier(
  ctx: RateContext,
  employment: EmploymentLabel,
  coverageGaps?: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): number | null {
  if (!ctx.isEba) return null;
  const resolved = findPenalty(ctx.penalties, 'PERMANENT_NIGHT_SHIFT', employment);
  if (resolved !== null) return resolved;
  if ((ctx.requiredBands ?? []).includes('PERMANENT_NIGHT_SHIFT')) {
    if (coverageGaps) {
      pushPenaltyCoverageGap(
        coverageGaps,
        'PERMANENT_NIGHT_SHIFT',
        employment,
        EBA_REQUIRED_PENALTY_COVERAGE_GAP_REASON,
      );
    }
    return COVERAGE_GAP_MULTIPLIER;
  }
  return null;
}

function findAllowance(
  allowances: AllowanceLine[],
  code: string,
  employment: EmploymentLabel | null,
): AllowanceLine | null {
  // Employment-specific first; fall back to employment-agnostic.
  const employmentKey = employment === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  const specific = allowances.find(
    (a) => a.allowanceCode === code && a.appliesToEmployment === employmentKey,
  );
  if (specific) return specific;
  const generic = allowances.find(
    (a) => a.allowanceCode === code && (a.appliesToEmployment ?? null) === null,
  );
  return generic ?? null;
}

function resolveAllowance(
  ctx: RateContext,
  code: string,
  employment: EmploymentLabel | null,
  coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): AllowanceLine | null {
  return resolveRequiredAllowance(ctx, code, employment, {
    lookup: (allowanceCode, lookupEmployment) =>
      findAllowance(ctx.allowances, allowanceCode, lookupEmployment as EmploymentLabel | null),
    coverageGaps,
  });
}

function conditionalOrdinaryMultiplier(employment: EmploymentLabel): number {
  return employment === 'CASUAL' ? 1 + CASUAL_LOADING_NURSES : 1.0;
}

type PenaltyLineWithLoading = PenaltyLine & { includesCasualLoading?: boolean };

/** P27 — skip compounding when the compiled penalty already embeds casual loading. */
export function nursesPenaltyIncludesCasualLoading(line: PenaltyLine | undefined): boolean {
  return (line as PenaltyLineWithLoading | undefined)?.includesCasualLoading === true;
}

/** Casual factor relative to the supplied base. Weekend callers pass the
 * resolved rate so a loaded hourly rate and a loading-inclusive penalty total
 * never count the same loading twice (EA cl 18.34, 22.3–22.4).
 * Other bands retain their existing clause-specific treatment.
 */
export function nursesCasualLoadingFactor(
  employment: EmploymentLabel,
  penaltyLine: PenaltyLine | undefined,
  _ctx?: RateContext,
  resolvedRate?: Pick<ResolvedNurseRate, 'includesCasualLoading'>,
): number {
  if (employment !== 'CASUAL') return 1.0;
  const loading = nursesPenaltyIncludesCasualLoading(penaltyLine) ? 1.0 : 1 + CASUAL_LOADING_NURSES;
  return resolvedRate?.includesCasualLoading === true
    ? loading / (1 + CASUAL_LOADING_NURSES)
    : loading;
}

/** Weekday ordinary uses a fixed 1.0 multiplier on the resolved base. A
 * WEEKDAY_DAY penalty marker cannot supply loading here because its numeric
 * multiplier is not used. Non-flat loaded rates have already been normalised
 * to the unloaded base by resolveNurseRate. */
export function nursesOrdinaryCasualFactor(
  employment: EmploymentLabel,
  resolvedRate?: Pick<ResolvedNurseRate, 'includesCasualLoading'>,
): number {
  if (employment !== 'CASUAL') return 1.0;
  if (resolvedRate?.includesCasualLoading === true) return 1.0;
  return 1 + CASUAL_LOADING_NURSES;
}

/**
 * Nurses EA cl 21.6 — a casual's overtime multiplier is a GREATER-OF: the
 * MA000034 cl 19.2 award-derived figure (permanent cl 19.1 tier x 1.25
 * casual loading) versus whatever the EA declares for the CASUAL band
 * (undertaking item 4's additive reading). Comparing them — instead of
 * simply trusting whichever the compiled overlay declared — stops an EA
 * declaring an additive casual OT band below the award floor from
 * under-paying cl 21.6's own test. F7 in
 * docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-nurses-ea-overtime-rate-basis-fix-shape.md
 * marks the EA-declared limb itself Path γ (no Medea Nurses EA casual OT
 * wage-rate row is evidenced), so in every currently certified fixture this
 * resolves to the award limb unchanged.
 */
function nursesCasualOvertimeGreaterOf(
  awardPermanentMultiplier: number,
  declaredCasualMultiplier: number,
): number {
  // No rounding here: these are MULTIPLIERS (e.g. 1.875), not dollars —
  // round2 would corrupt 1.875 to 1.88 and silently move every downstream
  // dollar pin by hours x baseRate x 0.005.
  const awardCasualMultiplier = awardPermanentMultiplier * (1 + CASUAL_LOADING_NURSES);
  return Math.max(awardCasualMultiplier, declaredCasualMultiplier);
}

/** P27 — tenant-configured OT tier multipliers with loaded-rate metadata (M2). */
export function nursesResolveOvertimeTierMultipliers(
  ctx: RateContext,
  employment: EmploymentLabel,
  coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): {
  tier1Mult: number;
  tier2Mult: number;
  sundayOTMult: number;
  phMultForOT: number;
} {
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);

  const tier1Line = findPenaltyLine(ctx, 'OVERTIME_FIRST_2H', employment);
  const tier2Line = findPenaltyLine(ctx, 'OVERTIME_AFTER_2H', employment);
  const sundayLine = findPenaltyLine(ctx, 'SUNDAY', employment);
  const phLine = findPenaltyLine(ctx, 'PUBLIC_HOLIDAY', employment);

  const tier1Base = resolveRequiredPenalty(ctx, 'OVERTIME_FIRST_2H', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const tier2Base = resolveRequiredPenalty(ctx, 'OVERTIME_AFTER_2H', employment, 2.0, {
    lookup: penaltyLookup,
    coverageGaps,
  });

  let tier1Mult = tier1Base * nursesCasualLoadingFactor(employment, tier1Line, ctx);
  let tier2Mult = tier2Base * nursesCasualLoadingFactor(employment, tier2Line, ctx);
  const sundayOTMult = 2.0 * nursesCasualLoadingFactor(employment, sundayLine, ctx);
  const phMultForOT = NURSES_PH_OT_MULT * nursesCasualLoadingFactor(employment, phLine, ctx);

  if (employment === 'CASUAL') {
    // Resolve the PERMANENT-band figure (or the MA000034 cl 19.1 hardcoded
    // default when no EA declares one) as the award-derivation baseline for
    // the greater-of test — never let a CASUAL-targeted EA declaration by
    // itself suppress the award floor. A scratch coverage-gaps array is used
    // here: OVERTIME_FIRST_2H/AFTER_2H are not in any profile's
    // ctxReadableBands, so this lookup can never legitimately raise a gap —
    // see award-rate-lookup.ts isRequiredPenaltyMissing.
    const scratchGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [];
    const tier1PermanentBase = resolveRequiredPenalty(ctx, 'OVERTIME_FIRST_2H', 'PERMANENT', 1.5, {
      lookup: penaltyLookup,
      coverageGaps: scratchGaps,
    });
    const tier2PermanentBase = resolveRequiredPenalty(ctx, 'OVERTIME_AFTER_2H', 'PERMANENT', 2.0, {
      lookup: penaltyLookup,
      coverageGaps: scratchGaps,
    });
    tier1Mult = nursesCasualOvertimeGreaterOf(tier1PermanentBase, tier1Mult);
    tier2Mult = nursesCasualOvertimeGreaterOf(tier2PermanentBase, tier2Mult);
  }

  return { tier1Mult, tier2Mult, sundayOTMult, phMultForOT };
}

/** Section 22 default; a generic tier block cannot prove cl 22.22 relief facts. */
function nursesCasualShiftworkerTier1(
  ctx: RateContext,
  input: Pick<ShiftPayInput, 'employmentType' | 'employeeCategory'>,
  existing: number,
): number {
  const applies =
    ctx.isEba === true &&
    (ctx.baseAwardCode ?? ctx.awardCode) === 'MA000034' &&
    input.employmentType === 'casual' &&
    input.employeeCategory === 'shiftworker' &&
    ctx.overtimeRules?.some((rule) => rule.sourceBlockCode === 'NEA_OVERTIME_TIERS');
  // Do not reduce higher contractual first/later tiers. Relief discount remains
  // unavailable until its notice-time and designated-relief evidence is wired.
  return applies ? Math.max(2.25, existing) : existing;
}

/** ENG1 R6: the EA and award limbs have independently valued hourly bases. */
function nursesEaShiftworkerOvertimeApplies(
  ctx: RateContext,
  input: ShiftPayWithOvertimeInput,
): boolean {
  return (
    ctx.isEba &&
    (ctx.baseAwardCode ?? ctx.awardCode) === 'MA000034' &&
    input.employeeCategory === 'shiftworker' &&
    ctx.overtimeRules?.some((rule) => rule.sourceBlockCode === 'NEA_OVERTIME_TIERS') === true
  );
}

function nursesEaOvertimeRate(args: {
  ctx: RateContext;
  input: ShiftPayWithOvertimeInput;
  base: number;
  loadedBase: boolean;
  awardMinimum: number;
  day: DayType;
  overtimeMinute: number;
}): number {
  const { ctx, input, day, overtimeMinute, awardMinimum } = args;
  const casual = input.employmentType === 'casual';
  const base = args.loadedBase && casual ? args.base / 1.25 : args.base;
  const exception = input.nursesOvertimeException;
  const flagged =
    exception?.approved === true &&
    exception.reason.trim().length > 0 &&
    overtimeMinute >= exception.startMinute &&
    overtimeMinute < exception.endMinute;
  const first =
    overtimeMinute + (input.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0) * 60 < 120;
  const awardMultiplier =
    day === 'publicHoliday' ? 3.125 : day === 'sunday' || !first ? 2.5 : 1.875;
  const primary = primaryOvertimeRuleFromContext(ctx);
  const normal =
    day === 'publicHoliday'
      ? 2.5
      : day === 'sunday'
        ? 2
        : first
          ? (primary?.firstTwoHoursMultiplier ?? 1.5)
          : (primary?.afterTwoHoursMultiplier ?? 2);
  // An approved swap/rotation removes only section 22's flat double-time limb.
  // Other applicable overtime entitlements and the independent award floor survive.
  const eaMultiplier = flagged ? normal + (casual ? 0.25 : 0) : casual ? 2.25 : 2;
  const contractual =
    day === 'publicHoliday' || day === 'sunday'
      ? undefined
      : findPenaltyLine(
          ctx,
          first ? 'OVERTIME_FIRST_2H' : 'OVERTIME_AFTER_2H',
          casual ? 'CASUAL' : 'PERMANENT',
        );
  const contractualRate = contractual
    ? base *
      contractual.multiplier *
      (casual && !nursesPenaltyIncludesCasualLoading(contractual) ? 1.25 : 1)
    : 0;
  return Math.max(
    base * eaMultiplier,
    casual ? awardMinimum * awardMultiplier : 0,
    contractualRate,
  );
}

function nursesUnderlyingAwardMinimum(
  ctx: RateContext,
  input: ShiftPayWithOvertimeInput & NursesShiftInputExtras,
  stream: 'nurse' | 'aged_care_nurse',
  gaps: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): number {
  if (input.employmentType !== 'casual') return 0;
  const eventDate = dateOnlyInTz(input.startTime, input.timezone ?? 'Australia/Sydney');
  const matching = (
    ctx.underlyingAwardVersions ?? (ctx.underlyingAward ? [ctx.underlyingAward] : [])
  ).filter(
    (floor) =>
      floor.effectiveFrom <= eventDate && (!floor.effectiveTo || floor.effectiveTo > eventDate),
  );
  const floor = matching.length === 1 ? matching[0] : null;
  // AE530670 Schedule 1 explicitly crosswalks these EA classifications to
  // MA000034 aged-care classifications. These are exact classification aliases,
  // not an inference about elapsed service, age, or supervisory duties.
  // Provenance: 2026-10-21-cap-nurses-national-minimum-crosswalk.md and
  // 2026-10-27-nurses-en-national-minimum-crosswalk.md.
  const aliases: Readonly<Record<string, { level: string; point: string | null }>> = {
    'rn 1 yr 1 casual': { level: 'RN_L1_AC', point: 'Y1' },
    'rn 1 yr 2 casual': { level: 'RN_L1_AC', point: 'Y1_TO_4' },
    'rn 1 yr 3 casual': { level: 'RN_L1_AC', point: 'Y1_TO_4' },
    'rn 1 yr 4 casual': { level: 'RN_L1_AC', point: 'Y1_TO_4' },
    'rn 1 yr 5 casual': { level: 'RN_L1_AC', point: 'Y4+' },
    'rn 1 yr 6 casual': { level: 'RN_L1_AC', point: 'Y4+' },
    'rn 1 yr 7 casual': { level: 'RN_L1_AC', point: 'Y4+' },
    'rn 1 yr 8 casual': { level: 'RN_L1_AC', point: 'Y4+' },
    'en 1 yr 1 casual': { level: 'EN_STUDENT_AC', point: 'U21' },
    'en 2 yr 1 casual': { level: 'EN_STUDENT_AC', point: '21+' },
    'en 2 yr 2 casual': { level: 'EN_SUPERVISING_AC', point: null },
  };
  const level = input.classificationLevel?.trim().toLowerCase();
  const point = input.classificationPayPoint?.trim().toLowerCase();
  const mapped =
    ctx.isEba &&
    ctx.ebaAgreementId &&
    ctx.ebaAgreementCode === 'AE530670' &&
    String(stream) === 'NURSES_EA' &&
    level &&
    level === point &&
    Object.hasOwn(aliases, level)
      ? aliases[level]
      : undefined;
  const minimumInput = mapped
    ? { ...input, classificationLevel: mapped.level, classificationPayPoint: mapped.point }
    : input;
  const resolved =
    floor && (mapped || stream === 'nurse' || stream === 'aged_care_nurse')
      ? resolveNurseRate(
          { ...ctx, isEba: false, rates: floor.rates },
          minimumInput,
          mapped ? 'aged_care_nurse' : stream,
          'CASUAL',
          gaps,
        )
      : null;
  if (!resolved?.recognised || !Number.isFinite(resolved.hourlyRate) || resolved.hourlyRate <= 0) {
    gaps.push({
      band: 'NURSES_CL21_6_AWARD_MINIMUM',
      employment: 'CASUAL',
      reason: 'NURSES_INDEPENDENT_AWARD_MINIMUM_REQUIRED',
    });
    return 0; // Provisional EA dollars only; coverage gap holds every payroll consumer.
  }
  return resolved.includesCasualLoading ? resolved.hourlyRate / 1.25 : resolved.hourlyRate;
}

/** P27 — cl 19.1(c) shift-loading backout on OT hours in loading bands (M4). */
export function nursesShiftLoadingOvertimeBackoutDollars(args: {
  otShiftBandHours: { eveningShift: number; nightShift: number };
  baseHourlyRate: number;
  ctx: RateContext;
  employment: EmploymentLabel;
  rawInput: Pick<ShiftPayInput, 'employeeCategory' | 'permanentNightShiftWorker'>;
  coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']>;
}): number {
  return nursesShiftLoadingOvertimeBackoutForBasis(args, false);
}

// The pre-fix basis is a compatibility floor only, pending Medea OT-loading
// adjudication (22 September M-OT-08); it is not a payable-rate oracle.
function nursesShiftLoadingOvertimeBackoutForBasis(
  args: Parameters<typeof nursesShiftLoadingOvertimeBackoutDollars>[0],
  preFix: boolean,
): number {
  const { otShiftBandHours, baseHourlyRate, ctx, employment, rawInput, coverageGaps } = args;
  const baseAwardCode = ctx.baseAwardCode ?? ctx.awardCode;
  if (retainShiftLoadingOnOvertimeForProfile(baseAwardCode)) return 0;
  if (otShiftBandHours.eveningShift <= 0 && otShiftBandHours.nightShift <= 0) return 0;
  if ((rawInput.employeeCategory ?? 'dayworker') !== 'shiftworker') return 0;

  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);

  let backout = 0;

  if (otShiftBandHours.nightShift > 0) {
    const nightBaseMult =
      rawInput.permanentNightShiftWorker === true
        ? (ebaPermanentNightMultiplier(ctx, employment, coverageGaps) ??
          resolveRequiredPenalty(ctx, 'WEEKDAY_NIGHT', employment, 1.15, {
            lookup: penaltyLookup,
            coverageGaps,
          }))
        : resolveRequiredPenalty(ctx, 'WEEKDAY_NIGHT', employment, 1.15, {
            lookup: penaltyLookup,
            coverageGaps,
          });
    const nightMult = preFix
      ? nightBaseMult *
        nursesCasualLoadingFactor(
          employment,
          findPenaltyLine(ctx, 'WEEKDAY_NIGHT', employment),
          ctx,
        )
      : nightBaseMult;
    backout += otShiftBandHours.nightShift * baseHourlyRate * (nightMult - 1);
  }

  if (otShiftBandHours.eveningShift > 0) {
    const afternoonBaseMult = resolveRequiredPenalty(ctx, 'WEEKDAY_AFTERNOON', employment, 1.125, {
      lookup: penaltyLookup,
      coverageGaps,
    });
    const afternoonMult = preFix
      ? afternoonBaseMult *
        nursesCasualLoadingFactor(
          employment,
          findPenaltyLine(ctx, 'WEEKDAY_AFTERNOON', employment),
          ctx,
        )
      : afternoonBaseMult;
    backout += otShiftBandHours.eveningShift * baseHourlyRate * (afternoonMult - 1);
  }

  return round2(backout);
}

function conditionalPenaltyExtraPay(input: {
  baseHourlyRate: number;
  hours: number;
  totalMultiplier: number;
  ordinaryMultiplier: number;
}): number {
  return round2(
    input.baseHourlyRate *
      Math.max(0, input.hours) *
      Math.max(0, input.totalMultiplier - input.ordinaryMultiplier),
  );
}

function requiredAllowanceMissing(
  ctx: RateContext,
  code: string,
  employment: EmploymentLabel | null,
): boolean {
  return isRequiredAllowanceMissing(ctx, code, employment, (allowanceCode, lookupEmployment) =>
    findAllowance(ctx.allowances, allowanceCode, lookupEmployment as EmploymentLabel | null),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Engine
// ═══════════════════════════════════════════════════════════════════════════

const ZERO_BREAKDOWN = (
  level: string | null,
  baseHourlyRate: number,
  warnings: string[],
): ShiftPayBreakdown => ({
  normalizedLevel: level as NormalizedSchadsLevel | null,
  baseHourlyRate,
  hoursWorked: 0,
  bands: {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  },
  basePay: 0,
  penaltyPay: 0,
  shiftLoadingPay: 0,
  overtimeHours: 0,
  overtimePay: 0,
  overtimeFirstHours: 0,
  overtimeRemainderHours: 0,
  overtimePublicHolidayHours: 0,
  allowancesPay: 0,
  allowanceLines: [],
  grossTotal: 0,
  warnings,
});

interface NursesShiftInputExtras {
  /** Stream selector — `'nurse'` for cl 15.1 (non-aged-care), `'aged_care_nurse'` for cl 15.3. */
  classificationStream?: 'nurse' | 'aged_care_nurse';
  /**
   * Explicit classification key for direct rate lookup (preferred over
   * parsing `schadsLevel`). Examples:
   *   - cl 15.1 RN L1 PP3 → `classificationLevel: 'RN_L1'`, `classificationPayPoint: 'PP3'`
   *   - cl 15.3 RN L4    → `classificationLevel: 'RN_L4_AC'`, `classificationPayPoint: null`
   *   - cl 15.3 EN supervising → `classificationLevel: 'EN_SUPERVISING_AC'`, `classificationPayPoint: null`
   * When set, the engine looks up the rate directly without parsing
   * `schadsLevel`. Avoids the "_AC suffix not in parser regex" class of
   * bugs and supports the full classification matrix verbatim.
   */
  classificationLevel?: string;
  classificationPayPoint?: string | null;
  /**
   * Phase 1D (Award Engine Integrity 2026-05) — worker's substantive
   * classification at shift date. Pass-through field used to populate
   * `breakdown.actingClassification` + `substantiveClassification` when a
   * higher-duties override has elevated the worker's `classificationLevel`
   * input above their substantive level. Caller (timesheet-actions /
   * snapshotPayRun / preflight) is responsible for resolving substantive
   * from `staff_classification_history` and passing it through unchanged.
   *
   * When this field is omitted OR matches the engine's resolved
   * classification → both `actingClassification` and `substantiveClassification`
   * on the breakdown are null (no override applied).
   */
  substantiveClassification?: ShiftClassificationSnapshot;
  /**
   * Phase 1F (Award Engine Integrity 2026-05) — majority-of-shift on-call
   * periods. Each entry produces ONE allowance line at the rate of the
   * dominant day type per FWO library K700647 worked example:
   * Fri 18:00 → Sat 10:00 (1h Fri + 16h Sat) pays Saturday rate ×1, NOT
   * pro-rated across the two days.
   *
   * Code mapping by dominantDayType from `classifyShift(..., dominantDayRule:
   * 'STRICT_MAJORITY')`:
   *   - weekday → ON_CALL_WEEKDAY
   *   - saturday → ON_CALL_SATURDAY
   *   - sunday or publicHoliday → ON_CALL_SUNDAY_PH (falls back to
   *     ON_CALL_WEEKEND if ON_CALL_SUNDAY_PH not seeded)
   *
   * Backward compat: when `onCallPeriods` is omitted/empty, the engine
   * falls through to the legacy `onCallWeekdayDays` + `onCallWeekendDays`
   * count fields. When BOTH are present, `onCallPeriods` wins (count
   * fields ignored to avoid double-pay).
   *
   * `isPublicHoliday` may be supplied per period by platform callers that
   * have local-date PH evidence. When omitted, the shift-level PH flag remains
   * the backward-compatible fallback.
   */
  onCallPeriods?: Array<{ startTime: Date; endTime: Date; isPublicHoliday?: boolean }>;
  /**
   * Phase 1O (Award Engine Integrity 2026-05-17) — if true, the employer
   * provided an adequate meal during OT (cl 17.3(b)(i) provision-of-meal
   * carve-out). Suppresses BOTH MEAL_ALLOWANCE and MEAL_OT_ADDITIONAL_ALLOWANCE
   * emissions. Defaults to `undefined`/`false` (no carve-out → meals fire
   * when OT thresholds are met). Coordinator UI / mobile data-capture is
   * W22-A.1 post-Madea; until then, callers don't set this and the engine
   * emits meal allowances when the OT-duration trigger fires (W22-A
   * conditional over-pay risk documented in CLAUDE.md "Known platform gaps").
   */
  mealProvidedDuringOT?: boolean | null;
  /**
   * Phase 1O (Award Engine Integrity 2026-05-17) — rare exceptional
   * carve-out per cl 17.3(b)(iii): if true, the worker could feasibly
   * return home for the meal within the meal break, so the allowance is
   * not payable. Suppresses BOTH MEAL_ALLOWANCE and MEAL_OT_ADDITIONAL_ALLOWANCE
   * emissions. Defaults to `undefined`/`false` (no carve-out). Symmetric
   * shape with Aged Care `AgedCareInputExtras.couldReturnHomeForMeal` (W4)
   * and HPSS `HpssInputExtras.couldReturnHomeForMeal` (W22-A) — encoded
   * INDEPENDENTLY per per-award isolation rule.
   */
  couldReturnHomeForMeal?: boolean | null;
}

const POSTGRADUATE_QUALIFICATION_TIERS = {
  NEA_POSTGRAD_CERTIFICATE_ALLOWANCE: {
    qualificationTier: 'POSTGRAD_HOSPITAL_OR_CERTIFICATE',
    percentage: 4,
  },
  NEA_POSTGRAD_DEGREE_ALLOWANCE: {
    qualificationTier: 'POSTGRAD_DIPLOMA_OR_NON_NURSING_DEGREE',
    percentage: 6.5,
  },
  NEA_POSTGRAD_MASTERS_DOCTORATE_ALLOWANCE: {
    qualificationTier: 'MASTERS_OR_DOCTORATE',
    percentage: 7.5,
  },
} as const;

function applyPostgraduateQualificationAllowance(
  breakdown: ShiftPayBreakdown,
  rawInput: ShiftPayInput & NursesShiftInputExtras,
  resolvedLevel: string,
): ShiftPayBreakdown {
  const entitlement = rawInput.postgraduateQualificationAllowance;
  if (!entitlement) return breakdown;

  const expected = POSTGRADUATE_QUALIFICATION_TIERS[entitlement.componentCode];
  const classificationEligible =
    resolvedLevel.startsWith('RN_') ||
    resolvedLevel.startsWith('EN_') ||
    resolvedLevel.startsWith('NP');
  const inputValid =
    Boolean(entitlement.assignmentId) &&
    entitlement.evidenceRef.trim().length >= 3 &&
    expected.qualificationTier === entitlement.qualificationTier &&
    expected.percentage === entitlement.percentage;
  if (!classificationEligible || !inputValid) {
    return {
      ...breakdown,
      coverageGaps: [
        ...(breakdown.coverageGaps ?? []),
        {
          band: 'POSTGRADUATE_QUALIFICATION_ALLOWANCE',
          allowanceCode: entitlement.componentCode,
          employment: rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT',
          reason: !classificationEligible
            ? 'Medea Nurses EA cl 18.4 permits the qualification allowance only for an eligible Registered or Enrolled Nurse.'
            : 'Medea Nurses EA cl 18.4 qualification tier, percentage or evidence identity is invalid.',
        },
      ],
      warnings: [
        ...breakdown.warnings,
        'Postgraduate qualification allowance was not applied because its governed eligibility input is invalid.',
      ],
    };
  }

  const percentageFactor = entitlement.percentage / 100;
  const ordinaryHours = round4(Math.max(0, breakdown.hoursWorked - breakdown.overtimeHours));
  const allowanceHourlyRate = breakdown.baseHourlyRate * percentageFactor;
  const ordinaryAmount = round2(ordinaryHours * allowanceHourlyRate);
  // Clause 18.6 expressly requires the qualification allowance to be taken
  // into account when overtime is calculated. The existing OT dollars carry
  // the exact day/tier/casual multipliers, so the percentage of that amount is
  // the separately itemised qualification component for the OT hours.
  const overtimeAmount = round2(breakdown.overtimePay * percentageFactor);
  const leaveLoadingLine = breakdown.allowanceLines.find(
    (line) => line.code === 'LEAVE_LOADING_ALLOWANCE',
  );
  const annualLeaveLoadingAmount =
    rawInput.segmentKind === 'paid_annual_leave'
      ? round2((leaveLoadingLine?.amount ?? 0) * percentageFactor)
      : 0;
  const totalAmount = round2(ordinaryAmount + overtimeAmount + annualLeaveLoadingAmount);
  if (totalAmount <= 0) return breakdown;

  const line: ShiftPayAllowanceLine = {
    code: entitlement.componentCode,
    componentCode: entitlement.componentCode,
    description: `Postgraduate qualification allowance (${entitlement.percentage}%)`,
    clauseRef: 'Medea Park Nurses Agreement 2024 cls 18.4-18.6',
    amount: totalAmount,
    units: round4(breakdown.hoursWorked),
    unitType: 'hours',
    taxable: true,
    superable: true,
  };
  return {
    ...breakdown,
    allowancesPay: round2(breakdown.allowancesPay + totalAmount),
    allowanceLines: [...breakdown.allowanceLines, line],
    grossTotal: sumMoney(breakdown.grossTotal, totalAmount),
    postgraduateQualificationAllowance: {
      assignmentId: entitlement.assignmentId,
      componentCode: entitlement.componentCode,
      qualificationTier: entitlement.qualificationTier,
      percentage: entitlement.percentage,
      relevantHourlyRate: breakdown.baseHourlyRate,
      allowanceHourlyRate,
      ordinaryHours,
      overtimeHours: round4(breakdown.overtimeHours),
      ordinaryAmount,
      overtimeAmount,
      annualLeaveLoadingAmount,
      totalAmount,
      evidenceRef: entitlement.evidenceRef,
    },
  };
}

/**
 * Phase 1D helper: returns `actingClassification` + `substantiveClassification`
 * pair for the breakdown. Both null when no override applied OR when the
 * engine's resolved classification matches the substantive. Both populated
 * when they differ.
 */
function resolveActingVsSubstantive(
  resolvedStream: string | null,
  resolvedLevel: string,
  resolvedPayPoint: string | null,
  substantive: ShiftClassificationSnapshot | undefined,
): {
  actingClassification: ShiftClassificationSnapshot | null;
  substantiveClassification: ShiftClassificationSnapshot | null;
} {
  if (!substantive) {
    return { actingClassification: null, substantiveClassification: null };
  }
  const sameAsActing =
    substantive.classificationStream === resolvedStream &&
    substantive.classificationLevel === resolvedLevel &&
    (substantive.payPoint ?? null) === (resolvedPayPoint ?? null);
  if (sameAsActing) {
    return { actingClassification: null, substantiveClassification: null };
  }
  return {
    actingClassification: {
      classificationStream: resolvedStream,
      classificationLevel: resolvedLevel,
      payPoint: resolvedPayPoint ?? null,
    },
    substantiveClassification: {
      classificationStream: substantive.classificationStream,
      classificationLevel: substantive.classificationLevel,
      payPoint: substantive.payPoint,
    },
  };
}

/**
 * Exact (unrounded) ordinary pay units that calculate() rounded into basePay
 * (weekday) and penaltyPay (Saturday, Sunday, public holiday), keyed by the
 * breakdown it returned. Never serialised. calculateWithOvertime backs out
 * overtime per unit from these (Astra RND-04/RND-07), so any other money in
 * the same component (e.g. a missed-meal penalty) is carried through exactly.
 */
const ORDINARY_PAY_UNITS = new WeakMap<
  ShiftPayBreakdown,
  { weekday: number; saturday: number; sunday: number; publicHoliday: number }
>();

function calculate(
  rawInput: ShiftPayInput & NursesShiftInputExtras,
  ctx?: RateContext,
): ShiftPayBreakdown {
  const warnings: string[] = [];

  if (!ctx) {
    warnings.push(
      'NursesEngine called without a RateContext — Phase 1 always loads ctx from DB; this path is for dev tests only.',
    );
    return ZERO_BREAKDOWN(null, 0, warnings);
  }

  const stream = rawInput.classificationStream ?? 'aged_care_nurse';
  if (!NURSES_STREAMS.has(stream)) {
    warnings.push(
      `NursesEngine called with classification stream '${stream}' — only 'nurse' and 'aged_care_nurse' are supported.`,
    );
    return ZERO_BREAKDOWN(null, 0, warnings);
  }

  const employment: EmploymentLabel = rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [];
  const resolved = resolveNurseRate(ctx, rawInput, stream, employment, coverageGaps);
  if (!resolved.recognised) {
    warnings.push(
      `Nurses classification "${rawInput.schadsLevel}" not recognised under stream '${stream}' — gross pay will be $0. Set the worker's classification to a canonical Nurses Award value (e.g. 'RN_L3' or 'EN_L1.1').`,
    );
  }

  const baseHourlyRate = resolved.hourlyRate;
  const normalizedLevel = resolved.recognised ? (resolved.level as NormalizedSchadsLevel) : null;

  // Total hours
  const totalMs = rawInput.endTime.getTime() - rawInput.startTime.getTime();
  if (totalMs <= 0) {
    warnings.push('Shift end must be after start — returning zero pay.');
    return ZERO_BREAKDOWN(normalizedLevel, baseHourlyRate, warnings);
  }

  const rawHours = totalMs / (1000 * 60 * 60);
  const deductibleBreakMinutes = payrollBreakMinutesForEngine(
    rawInput.breakMinutes ?? 0,
    rawInput.breakTreatment,
    rawInput.breakSemantics,
  );
  const breakHours = deductibleBreakMinutes / 60;
  const hoursWorked = Math.max(0, rawHours - breakHours);
  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const employeeCategory = rawInput.employeeCategory ?? 'dayworker';
  const isShiftworker = employeeCategory === 'shiftworker';

  if (rawInput.publicHolidayNotWorked) {
    const publicHolidayNotWorkedBlockCode = conditionalPenaltyBlockCodeForKind(
      ctx,
      'PUBLIC_HOLIDAY_NOT_WORKED',
      rawInput.conditionalPenaltyCode ?? null,
      employeeCategory,
    );
    const line = findConditionalPenaltyLine(ctx, 'PUBLIC_HOLIDAY_NOT_WORKED', employment, {
      blockCode: publicHolidayNotWorkedBlockCode,
      employeeCategory,
    });
    if (!line) {
      pushMissingConditionalPenaltyGap(
        coverageGaps,
        ctx,
        'PUBLIC_HOLIDAY_NOT_WORKED',
        employment,
        publicHolidayNotWorkedBlockCode,
        employeeCategory,
      );
    }
    const paidHours = rawInput.usualRosteredHours ?? hoursWorked;
    const publicHolidayNotWorkedPay = line
      ? round2(paidHours * baseHourlyRate * line.multiplier)
      : 0;
    return applyPostgraduateQualificationAllowance(
      {
        normalizedLevel,
        baseHourlyRate,
        hoursWorked: round4(paidHours),
        bands: {
          weekdayOrdinary: 0,
          saturdayOrdinary: 0,
          sundayOrdinary: 0,
          publicHoliday: round4(paidHours),
          eveningShift: 0,
          nightShift: 0,
        },
        basePay: publicHolidayNotWorkedPay,
        penaltyPay: 0,
        shiftLoadingPay: 0,
        overtimeHours: 0,
        overtimePay: 0,
        overtimeFirstHours: 0,
        overtimeRemainderHours: 0,
        overtimePublicHolidayHours: 0,
        allowancesPay: 0,
        allowanceLines: [],
        grossTotal: publicHolidayNotWorkedPay,
        warnings,
        ...coverageGapsField(coverageGaps),
      },
      rawInput,
      resolved.level,
    );
  }

  // Phase 1A.3 (Award Engine Integrity 2026-05) — replaced per-segment
  // classification with whole-shift majority + finish-time character per
  // FWO library K700647 and Nurses cl 26/27.2:
  //
  //   - dominantDayType (STRICT_MAJORITY rule) selects ONE penalty band
  //     for the whole shift's paid hours. Fri 23:00 → Sat 07:00 (1h Fri +
  //     7h Sat) now pays the whole 8h at Saturday rate, not 1h weekday +
  //     7h Saturday.
  //
  //   - shiftCharacter (cl 27.2 whole-shift afternoon/night) drives the
  //     loading band for shiftworker weekday shifts. A 14:00-22:00 shift
  //     finishes at 22:00 → AFTERNOON for the whole 8h (was previously
  //     misclassified as half afternoon + half night by start-hour bands).
  //
  // The architecture decisions doc D1 (15 May 2026) recorded
  // STRICT_MAJORITY as the Nurses default with EBA-driven exceptions
  // routed via the eba-scope-resolver (not a tenant config knob).
  const classificationTimes = shiftClassificationGeometry(rawInput);
  const classified = classifyShift({
    startTime: classificationTimes.startTime,
    endTime: classificationTimes.endTime,
    breakMinutes: classificationTimes.breakMinutes,
    timezone: tz,
    isPublicHoliday: rawInput.isPublicHoliday ?? false,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    // EA-supplied finish-time windows (Nurses EA cl 22.1) when present; else the
    // base-award cl 20.1 defaults. Data-driven so each agreement defines its own.
    shiftCharacterRules: ctx.shiftCharacterRules ?? NURSES_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker:
      rawInput.permanentNightShiftWorker === true &&
      ebaPermanentNightMultiplier(ctx, employment) !== null,
  });

  // Aggregate paid hours by band (mirrors ShiftPayBreakdown.bands shape).
  const bands = {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0, // afternoon shift loading (cl 27.2(a))
    nightShift: 0, // night shift loading (cl 27.2(b) / cl 20.2(c) permanent night)
  };

  // Allocate ALL paid hours to the single dominant day-type band.
  switch (classified.dominantDayType) {
    case 'publicHoliday':
      bands.publicHoliday = hoursWorked;
      break;
    case 'saturday':
      bands.saturdayOrdinary = hoursWorked;
      break;
    case 'sunday':
      bands.sundayOrdinary = hoursWorked;
      break;
    case 'weekday':
      bands.weekdayOrdinary = hoursWorked;
      // Cl 27.2 / 20.2(c) — whole-shift afternoon or night loading on top
      // of weekday rate (shiftworkers only). Loadings stack with the
      // weekday band: the shift gets paid (1.0 × base for weekdayOrdinary)
      // PLUS (loading - 1.0) × base via shiftLoadingPay below.
      if (isShiftworker) {
        if (classified.shiftCharacter === 'AFTERNOON') {
          bands.eveningShift = hoursWorked;
        } else if (
          classified.shiftCharacter === 'NIGHT' ||
          classified.shiftCharacter === 'PERMANENT_NIGHT'
        ) {
          bands.nightShift = hoursWorked;
        }
      }
      break;
  }

  // For RN L4/L5 — strip every penalty and shift loading, pay flat base for all hours.
  if (resolved.excludedFromPenalties) {
    const flatBase = round2(hoursWorked * baseHourlyRate);
    return applyPostgraduateQualificationAllowance(
      {
        normalizedLevel,
        baseHourlyRate,
        hoursWorked: round4(hoursWorked),
        bands: {
          ...bands,
          // Reset penalties — L4/L5 are paid base for everything.
          saturdayOrdinary: 0,
          sundayOrdinary: 0,
          publicHoliday: 0,
          eveningShift: 0,
          nightShift: 0,
          weekdayOrdinary: round4(hoursWorked),
        },
        basePay: flatBase,
        penaltyPay: 0,
        shiftLoadingPay: 0,
        overtimeHours: 0,
        overtimePay: 0,
        overtimeFirstHours: 0,
        overtimeRemainderHours: 0,
        overtimePublicHolidayHours: 0,
        allowancesPay: 0,
        allowanceLines: [],
        grossTotal: flatBase,
        warnings,
      },
      rawInput,
      resolved.level,
    );
  }

  // Penalty multipliers from ctx.
  //
  // Phase 1J (2026-05-16) — PH multiplier handling split:
  //   - `phMult` here is the cl 28.2(a) ORDINARY-PH rate (= 2.00 per
  //     primary, NOT 2.50). DB seed was previously 2.50 attributed
  //     to "cl 19.1(a)(iii) OT rate"; the data-integrity correction
  //     migration (20267077230000_correct_nurses_ph_ordinary_multiplier.sql)
  //     reseeds to 2.00. Engine ordinary-path reads it here.
  //   - cl 19.1(a)(iii) OT rate of 2.50 is encoded as the engine constant
  //     NURSES_PH_OT_MULT below the calculate fn; the OT path uses that
  //     constant directly. See errata catalogue MD-NURSES-A7.
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);
  const satLine = findPenaltyLine(ctx, 'SATURDAY', employment);
  const sunLine = findPenaltyLine(ctx, 'SUNDAY', employment);
  const phLine = findPenaltyLine(ctx, 'PUBLIC_HOLIDAY', employment);
  const satMult = resolveRequiredPenalty(ctx, 'SATURDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const sunMult = resolveRequiredPenalty(ctx, 'SUNDAY', employment, 1.75, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const phMult = resolveRequiredPenalty(ctx, 'PUBLIC_HOLIDAY', employment, 2.0, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const afternoonMult = resolveRequiredPenalty(ctx, 'WEEKDAY_AFTERNOON', employment, 1.125, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const permanentNightMult = ebaPermanentNightMultiplier(ctx, employment, coverageGaps);
  const nightMult =
    rawInput.permanentNightShiftWorker === true && permanentNightMult !== null
      ? permanentNightMult
      : resolveRequiredPenalty(ctx, 'WEEKDAY_NIGHT', employment, 1.15, {
          lookup: penaltyLookup,
          coverageGaps,
        });

  // Phase 1J (Award Engine Integrity 2026-05-16) — casual loading is
  // PER-CLAUSE in MA000034. cl 11.4 is narrowly scoped to cl 20 shiftwork
  // loadings ONLY; cl 19 OT, cl 21 weekend penalties, cl 28 PH ordinary
  // are MULTIPLICATIVE ("X% of the casual hourly rate"):
  //
  //   Sat casual ordinary (cl 21.1) = satMult × base × 1.25  → 1.5  × 1.25 = 1.875× base
  //   Sun casual ordinary (cl 21.2) = sunMult × base × 1.25  → 1.75 × 1.25 = 2.1875× base
  //   PH casual ordinary  (cl 28.2(a)) = phMult × base × 1.25 → 2.0  × 1.25 = 2.500× base
  //                                                              (phMult from ctx = 2.0
  //                                                               post-Phase-1J seed)
  //
  // For cl 11.1 ordinary hours: `1.0 × 1.25 = 1.25 = 1.0 + 0.25` — additive
  // and multiplicative give the same answer in this special case, so the
  // model is consistent.
  //
  // For cl 20.2(a)/(b) shiftwork loadings (afternoon +12.5%, night +15%):
  // ADDITIVE per cl 11.4 — handled below as a `(loadingMult - 1)` delta on
  // top of the weekday-ordinary band, NOT scaled by casualMult.
  //
  // See errata catalogue entry for the cl 11.4 narrow-scope reframing +
  // Phase 1J fix-shape verification artifact for the per-multiplier
  // primary-source citations.
  const weekdayOrdRate = 1.0 * nursesOrdinaryCasualFactor(employment, resolved);
  const satRate = satMult * nursesCasualLoadingFactor(employment, satLine, ctx);
  const sunRate = sunMult * nursesCasualLoadingFactor(employment, sunLine, ctx);
  const phRate = phMult * nursesCasualLoadingFactor(employment, phLine, ctx);

  const ordinaryPayUnits = {
    weekday: bands.weekdayOrdinary * baseHourlyRate * weekdayOrdRate,
    saturday: bands.saturdayOrdinary * baseHourlyRate * satRate,
    sunday: bands.sundayOrdinary * baseHourlyRate * sunRate,
    publicHoliday: bands.publicHoliday * baseHourlyRate * phRate,
  };
  const basePay = round2(ordinaryPayUnits.weekday);
  const satPay = round2(ordinaryPayUnits.saturday);
  const sunPay = round2(ordinaryPayUnits.sunday);
  const phPay = round2(ordinaryPayUnits.publicHoliday);
  let penaltyPay = round2(satPay + sunPay + phPay);

  // Shift loadings (afternoon / night) are additive on top of weekday ordinary.
  const afternoonLoadingExtra = round2(bands.eveningShift * baseHourlyRate * (afternoonMult - 1));
  const nightLoadingExtra = round2(bands.nightShift * baseHourlyRate * (nightMult - 1));
  const shiftLoadingPay = round2(afternoonLoadingExtra + nightLoadingExtra);

  if (rawInput.missedMealBreak) {
    const mealBreakBlockCode = conditionalPenaltyBlockCodeForKind(
      ctx,
      'MEAL_BREAK',
      rawInput.conditionalPenaltyCode ?? null,
      employeeCategory,
    );
    const line = findConditionalPenaltyLine(ctx, 'MEAL_BREAK', employment, {
      blockCode: mealBreakBlockCode,
      employeeCategory,
    });
    if (line) {
      const trigger = line.triggerContinuousHours ?? 5;
      const continuousHours = rawInput.continuousHoursWithoutMealBreak ?? hoursWorked;
      penaltyPay = round2(
        penaltyPay +
          conditionalPenaltyExtraPay({
            baseHourlyRate,
            hours: Math.max(0, continuousHours - trigger),
            totalMultiplier: line.multiplier,
            ordinaryMultiplier: conditionalOrdinaryMultiplier(employment),
          }),
      );
    } else {
      pushMissingConditionalPenaltyGap(
        coverageGaps,
        ctx,
        'MEAL_BREAK',
        employment,
        mealBreakBlockCode,
        employeeCategory,
      );
    }
  }

  if (rawInput.interruptedMealBreak) {
    const interruptedMealBreakBlockCode = conditionalPenaltyBlockCodeForKind(
      ctx,
      'INTERRUPTED_MEAL_BREAK',
      rawInput.conditionalPenaltyCode ?? null,
      employeeCategory,
    );
    const line = findConditionalPenaltyLine(ctx, 'INTERRUPTED_MEAL_BREAK', employment, {
      blockCode: interruptedMealBreakBlockCode,
      employeeCategory,
    });
    if (line) {
      penaltyPay = round2(
        penaltyPay +
          conditionalPenaltyExtraPay({
            baseHourlyRate,
            hours: (rawInput.interruptedMealBreakMinutes ?? 30) / 60,
            totalMultiplier: line.multiplier,
            ordinaryMultiplier: conditionalOrdinaryMultiplier(employment),
          }),
      );
    } else {
      pushMissingConditionalPenaltyGap(
        coverageGaps,
        ctx,
        'INTERRUPTED_MEAL_BREAK',
        employment,
        interruptedMealBreakBlockCode,
        employeeCategory,
      );
    }
  }

  // Allowances from ctx
  const allowanceLines: ShiftPayAllowanceLine[] = [];
  let allowancesPay = 0;

  // Sleepover — Nurses Award has none; do not seed; explicitly skipped here.

  // Travel KM
  // Phase 1N (2026-05-17) — canonical emission code; DB lookup key 'KM' unchanged.
  if ((rawInput.travelKm ?? 0) > 0) {
    const km = resolveAllowance(ctx, 'KM', employment, coverageGaps);
    if (km) {
      const amt = round2((rawInput.travelKm ?? 0) * km.amount);
      allowancesPay = round2(allowancesPay + amt);
      allowanceLines.push({
        code: 'KM_ALLOWANCE',
        description: km.description ?? 'Travel km',
        amount: amt,
        clauseRef: km.clauseRef ?? null,
      });
    }
  }

  // Phase 1F (Award Engine Integrity 2026-05) — majority-of-shift on-call
  // periods per FWO library K700647 + Nurses cl 17.2(a). When the caller
  // provides `onCallPeriods`, each period's dominant day type selects the
  // allowance code; the whole period is paid at that day's rate (no
  // pro-rating across days). When `onCallPeriods` is absent, fall through
  // to the legacy count fields (`onCallWeekdayDays` / `onCallWeekendDays`).
  // Both present → `onCallPeriods` wins to avoid double-pay.
  const onCallPeriods = rawInput.onCallPeriods ?? [];
  const useMajorityPath = onCallPeriods.length > 0;
  if (useMajorityPath) {
    for (const period of onCallPeriods) {
      if (
        !period.startTime ||
        !period.endTime ||
        period.endTime.getTime() <= period.startTime.getTime()
      ) {
        warnings.push(
          'On-call period has invalid start/end times — skipped. Verify the on-call register input.',
        );
        continue;
      }
      const classifiedPeriod = classifyShift({
        startTime: period.startTime,
        endTime: period.endTime,
        breakMinutes: 0,
        timezone: tz,
        isPublicHoliday: period.isPublicHoliday ?? rawInput.isPublicHoliday ?? false,
        shiftCharacterRules: NURSES_SHIFT_RULES,
        dominantDayRule: 'STRICT_MAJORITY',
      });
      // Map dominant day type → DB lookup key + canonical emission code per
      // Nurses cl 17.2(a). Phase 1N: emission codes use canonical
      // _ALLOWANCE-suffixed names; DB lookup keys unchanged.
      let allowance: AllowanceLine | null = null;
      let canonicalCode: string;
      switch (classifiedPeriod.dominantDayType) {
        case 'weekday':
          allowance = resolveAllowance(ctx, 'ON_CALL_WEEKDAY', employment, coverageGaps);
          canonicalCode = 'ON_CALL_WEEKDAY_ALLOWANCE';
          break;
        case 'saturday':
          allowance = resolveAllowance(ctx, 'ON_CALL_SATURDAY', employment, coverageGaps);
          if (!allowance && !requiredAllowanceMissing(ctx, 'ON_CALL_SATURDAY', employment)) {
            allowance = resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
          }
          canonicalCode = 'ON_CALL_SATURDAY_ALLOWANCE';
          break;
        case 'sunday':
        case 'publicHoliday':
          allowance = resolveAllowance(ctx, 'ON_CALL_SUNDAY_PH', employment, coverageGaps);
          if (!allowance && !requiredAllowanceMissing(ctx, 'ON_CALL_SUNDAY_PH', employment)) {
            allowance = resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
          }
          canonicalCode = 'ON_CALL_SUNDAY_PH_ALLOWANCE';
          break;
      }
      if (allowance) {
        const amt = round2(allowance.amount);
        allowancesPay = round2(allowancesPay + amt);
        allowanceLines.push({
          code: canonicalCode,
          description:
            allowance.description ??
            `On-call (${classifiedPeriod.dominantDayType}) — majority-of-period (FWO K700647)`,
          amount: amt,
          clauseRef: allowance.clauseRef ?? 'Nurses Award MA000034 cl 17.2(a)',
        });
      }
    }
  }

  // Backward-compat path: only consulted when onCallPeriods is empty.
  // Existing callers (snapshotPayRun, recompute, preflight) pass count
  // fields via the timesheet row until the on-call register ships and
  // adopts the onCallPeriods shape.
  const oncallWeekdayDays = useMajorityPath ? 0 : (rawInput.onCallWeekdayDays ?? 0);
  const oncallWeekendDays = useMajorityPath ? 0 : (rawInput.onCallWeekendDays ?? 0);
  if (oncallWeekdayDays > 0) {
    const oc = resolveAllowance(ctx, 'ON_CALL_WEEKDAY', employment, coverageGaps);
    if (oc) {
      const amt = round2(oncallWeekdayDays * oc.amount);
      allowancesPay = round2(allowancesPay + amt);
      allowanceLines.push({
        code: 'ON_CALL_WEEKDAY_ALLOWANCE',
        description: oc.description ?? 'On-call (weekday)',
        amount: amt,
        clauseRef: oc.clauseRef ?? null,
      });
    }
  }
  if (oncallWeekendDays > 0) {
    // Phase 1 Deep-Fix PR #3 (P1-A) — Nurses MA000034 cl 17.2(a)(i):
    //   ON_CALL_WEEKDAY   = weekday-only rate
    //   ON_CALL_SATURDAY  = Saturday-only rate (Saturdays that are NOT
    //                       also a public holiday)
    //   ON_CALL_SUNDAY_PH = Sunday OR public holiday rate
    //
    // The DB `timesheets` row stores `on_call_weekend_days` as a count
    // (0/1/2) without a Sat-vs-Sun discriminant column. Pre-fix, the
    // engine resolved `oc = ocSun ?? ocSat ?? ocFallback` (prefer Sunday
    // rate) → every Saturday-only on-call over-paid the Sunday rate
    // ($48.08 instead of $41.21). This fix derives the Sat-vs-Sun
    // discriminant from the SHIFT DATE (`rawInput.startTime` classified
    // in tenant tz) and emits two lines when the count is 2 (Sat + Sun).
    //
    // Saturday-PH case: per cl 17.2(a)(i), the Sun/PH rate applies on a
    // Saturday that is ALSO a public holiday (PH wins). This mirrors the
    // Phase 1F majority-of-shift path's `case 'publicHoliday'` branch.
    //
    // Phase 1F (`onCallPeriods`) remains the post-Madea proper solution —
    // the count-based backward-compat path is the bridge until the
    // on-call register lands.
    const isPublicHoliday = rawInput.isPublicHoliday ?? false;
    const shiftDayOfWeek = getDay(rawInput.startTime, tz); // 0=Sun, 6=Sat

    /**
     * Pick the right allowance for a single weekend on-call day.
     *
     * cl 17.2(a)(i) PH-wins rule: any public holiday → Sun/PH rate
     * regardless of weekday. Otherwise the weekday selects the rate.
     * Falls back to ON_CALL_WEEKEND (legacy pre-cutover seed data) when
     * the specific Sat/Sun key isn't present.
     */
    const pickWeekendAllowance = (preferSat: boolean) => {
      if (isPublicHoliday) {
        const sundayPh = resolveAllowance(ctx, 'ON_CALL_SUNDAY_PH', employment, coverageGaps);
        if (sundayPh || requiredAllowanceMissing(ctx, 'ON_CALL_SUNDAY_PH', employment)) {
          return sundayPh;
        }
        return resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
      }
      if (preferSat) {
        const saturday = resolveAllowance(ctx, 'ON_CALL_SATURDAY', employment, coverageGaps);
        if (saturday || requiredAllowanceMissing(ctx, 'ON_CALL_SATURDAY', employment)) {
          return saturday;
        }
        return resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
      }
      const sundayPh = resolveAllowance(ctx, 'ON_CALL_SUNDAY_PH', employment, coverageGaps);
      if (sundayPh || requiredAllowanceMissing(ctx, 'ON_CALL_SUNDAY_PH', employment)) {
        return sundayPh;
      }
      return resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
    };

    /**
     * Canonical emission code per the resolved allowance + PH-wins rule.
     */
    const canonicalCodeFor = (oc: AllowanceLine, preferSat: boolean) => {
      if (isPublicHoliday) {
        return 'ON_CALL_SUNDAY_PH_ALLOWANCE';
      }
      if (oc.allowanceCode === 'ON_CALL_SATURDAY') return 'ON_CALL_SATURDAY_ALLOWANCE';
      if (oc.allowanceCode === 'ON_CALL_SUNDAY_PH') return 'ON_CALL_SUNDAY_PH_ALLOWANCE';
      // ocFallback resolved; emit the Sat-or-Sun code consistent with the
      // intent so the audit pack + Xero export can categorise correctly.
      return preferSat ? 'ON_CALL_SATURDAY_ALLOWANCE' : 'ON_CALL_SUNDAY_PH_ALLOWANCE';
    };

    const emit = (oc: AllowanceLine, code: string, fallbackDesc: string) => {
      const amt = round2(oc.amount);
      allowancesPay = round2(allowancesPay + amt);
      allowanceLines.push({
        code,
        description: oc.description ?? fallbackDesc,
        amount: amt,
        clauseRef: oc.clauseRef ?? 'Nurses Award MA000034 cl 17.2(a)(i)',
      });
    };

    if (oncallWeekendDays === 1) {
      // Single weekend day — discriminate by shift date.
      // shiftDayOfWeek: 6=Sat, 0=Sun. PH-wins overrides both.
      const preferSat = shiftDayOfWeek === 6 && !isPublicHoliday;
      const oc = pickWeekendAllowance(preferSat);
      if (oc) {
        emit(oc, canonicalCodeFor(oc, preferSat), 'On-call (weekend)');
      }
    } else if (oncallWeekendDays === 2) {
      // Both days of weekend on-call — pay each rate separately so the
      // canonical Sat + Sun emission codes round-trip correctly through
      // audit pack + Xero export. PH-wins still applies if isPublicHoliday
      // is set (rare combination but handle it: both lines collapse to
      // Sun/PH rate per cl 17.2(a)(i)).
      if (isPublicHoliday) {
        // Both days treated as PH-equivalent → emit 2× Sun/PH line.
        let oc = resolveAllowance(ctx, 'ON_CALL_SUNDAY_PH', employment, coverageGaps);
        if (!oc && !requiredAllowanceMissing(ctx, 'ON_CALL_SUNDAY_PH', employment)) {
          oc = resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps);
        }
        if (oc) {
          const amt = round2(2 * oc.amount);
          allowancesPay = round2(allowancesPay + amt);
          allowanceLines.push({
            code: 'ON_CALL_SUNDAY_PH_ALLOWANCE',
            description: oc.description ?? 'On-call (Sunday/PH)',
            amount: amt,
            clauseRef: oc.clauseRef ?? 'Nurses Award MA000034 cl 17.2(a)(i)',
          });
        }
      } else {
        const ocSat = resolveAllowance(ctx, 'ON_CALL_SATURDAY', employment, coverageGaps);
        const ocSun = resolveAllowance(ctx, 'ON_CALL_SUNDAY_PH', employment, coverageGaps);
        const requiredSpecificMissing =
          requiredAllowanceMissing(ctx, 'ON_CALL_SATURDAY', employment) ||
          requiredAllowanceMissing(ctx, 'ON_CALL_SUNDAY_PH', employment);
        if (ocSat) {
          emit(ocSat, 'ON_CALL_SATURDAY_ALLOWANCE', 'On-call (Saturday)');
        }
        if (ocSun) {
          emit(ocSun, 'ON_CALL_SUNDAY_PH_ALLOWANCE', 'On-call (Sunday/PH)');
        }
        // Pre-cutover fallback: neither Sat nor Sun key present → emit a
        // single weekend line at 2× the fallback rate so workers still
        // get paid (the canonical emission code degrades gracefully).
        const ocFallback =
          !ocSat && !ocSun && !requiredSpecificMissing
            ? resolveAllowance(ctx, 'ON_CALL_WEEKEND', employment, coverageGaps)
            : null;
        if (!ocSat && !ocSun && ocFallback) {
          const amt = round2(2 * ocFallback.amount);
          allowancesPay = round2(allowancesPay + amt);
          allowanceLines.push({
            code: 'ON_CALL_WEEKEND_ALLOWANCE',
            description: ocFallback.description ?? 'On-call (weekend)',
            amount: amt,
            clauseRef: ocFallback.clauseRef ?? 'Nurses Award MA000034 cl 17.2(a)(i)',
          });
        }
      }
    }
  }

  // Meal allowance (cl 17.3(b)) — Phase 1O (Award Engine Integrity
  // 2026-05-17) MOVED OUT of OT-unaware calculate() and INTO OT-aware
  // calculateWithOvertime() so emission triggers on the OT-duration
  // threshold (cl 17.3(b)(i): OT > 1h; cl 17.3(b)(ii): OT > 4h for further
  // meal) rather than a caller `hasDelayedMealAllowance` boolean. Mirrors
  // the W4 Aged Care implementation. See NURSES_MEAL_OT_EMISSION in
  // award-data/nurses.ts for the SSOT constant.
  //
  // Backward compat note: pre-Phase-1O code path emitted ONE meal allowance
  // when `hasDelayedMealAllowance=true`, regardless of OT. This silently
  // dropped the cl 17.3(b)(ii) $14.98 further-meal allowance on every
  // nurse OT > 4h shift (W22 P0 wage-theft criminal regime exposure).
  // The new code path correctly emits both meals when OT > 4h, and
  // suppresses both when the cl 17.3(b)(iii) "could return home" carve-out
  // applies.
  //
  // Callers passing `hasDelayedMealAllowance=true` on a NO-OT shift will
  // now receive NO meal emission — this is the correct cl 17.3(b)(i)
  // behavior ("when required to work OVERTIME after the usual finishing
  // hour"). Test fixtures updated accordingly.

  // Uniform / laundry — same shape as SCHADS but Nurses-specific values.
  // Phase 1N: canonical emission codes; DB lookup keys unchanged.
  if (rawInput.hasUniformAllowance) {
    const u = resolveAllowance(ctx, 'UNIFORM_PER_SHIFT', employment, coverageGaps);
    if (u) {
      allowancesPay = round2(allowancesPay + u.amount);
      allowanceLines.push({
        code: 'UNIFORM_ALLOWANCE',
        description: u.description ?? 'Uniform allowance',
        amount: u.amount,
        clauseRef: u.clauseRef ?? null,
      });
    }
  }
  if (rawInput.hasLaundryAllowance) {
    const l = resolveAllowance(ctx, 'LAUNDRY_PER_SHIFT', employment, coverageGaps);
    if (l) {
      allowancesPay = round2(allowancesPay + l.amount);
      allowanceLines.push({
        code: 'LAUNDRY_ALLOWANCE',
        description: l.description ?? 'Laundry allowance',
        amount: l.amount,
        clauseRef: l.clauseRef ?? null,
      });
    }
  }

  // First-aid responsibility — Nurses Award does not have this allowance
  // (first-aid is part of clinical practice, not a separate allowance).
  // Skipped intentionally; SCHADS workers get it via the SchadsEngine.

  // Phase 3 PR #3 sub-phase 3G (2026-05-18) — Annual leave loading per
  // MA000034 cl 22.5(a). Fires when segmentKind === 'paid_annual_leave' AND
  // employmentType is non-casual (NES s.86 — casuals receive 25% loading on
  // ordinary hours in lieu, not annual leave). Naive 17.5% × ordinary leave
  // pay (hoursWorked × baseHourlyRate, un-penalty-loaded — leave is paid at
  // ordinary time per cl 22.5(a)).
  //
  // Clause ref specialist-verified 2026-05-19 (Codex Pass A retrospective
  // P2-N). Pre-fix cited cl 33.4(a) which does not exist as annual leave
  // loading in MA000034. Primary source: FWC consolidated MA000034 cl 22.5
  // (annual leave loading); PayCat / Williamson Barwick / FWO Library
  // MA000034 confirm.
  //
  // cl 22.5(b) shiftworker higher-of test is applied by the pay-run
  // leave-snapshot layer from rostered shift evidence. This engine branch
  // emits the flat cl 22.5(a) input for that comparison.
  if (rawInput.segmentKind === 'paid_annual_leave' && rawInput.employmentType !== 'casual') {
    const ordinaryLeavePay = round2(hoursWorked * baseHourlyRate);
    const loadingAmount = round2(ordinaryLeavePay * NURSES_LEAVE.annualLeaveLoading);
    if (loadingAmount > 0) {
      allowancesPay = round2(allowancesPay + loadingAmount);
      allowanceLines.push({
        code: 'LEAVE_LOADING_ALLOWANCE',
        description: 'Annual leave loading (17.5%)',
        amount: loadingAmount,
        clauseRef: NURSES_LEAVE.clauseRef,
      });
    }
  }

  const grossTotal = sumMoney(basePay, penaltyPay, shiftLoadingPay, allowancesPay);

  const classificationPair = resolveActingVsSubstantive(
    stream,
    resolved.level,
    resolved.payPoint,
    rawInput.substantiveClassification,
  );

  const result = applyPostgraduateQualificationAllowance(
    {
      normalizedLevel,
      baseHourlyRate,
      hoursWorked: round4(hoursWorked),
      bands: {
        weekdayOrdinary: round4(bands.weekdayOrdinary),
        saturdayOrdinary: round4(bands.saturdayOrdinary),
        sundayOrdinary: round4(bands.sundayOrdinary),
        publicHoliday: round4(bands.publicHoliday),
        eveningShift: round4(bands.eveningShift),
        nightShift: round4(bands.nightShift),
      },
      basePay,
      penaltyPay,
      shiftLoadingPay,
      overtimeHours: 0,
      overtimePay: 0,
      overtimeFirstHours: 0,
      overtimeRemainderHours: 0,
      overtimePublicHolidayHours: 0,
      allowancesPay,
      allowanceLines,
      grossTotal,
      warnings,
      ...coverageGapsField(coverageGaps),
      actingClassification: classificationPair.actingClassification,
      substantiveClassification: classificationPair.substantiveClassification,
    },
    rawInput,
    resolved.level,
  );
  rememberEnginePricingEvidence(result, {
    ordinaryAllocation: 'WHOLE_SHIFT',
    segments: classified.segments.map((s) => ({
      start: s.startTime.toISOString(),
      end: s.endTime.toISOString(),
      day: s.dayType,
    })),
    ordinaryRates: {
      weekday: baseHourlyRate * weekdayOrdRate,
      saturday: baseHourlyRate * satRate,
      sunday: baseHourlyRate * sunRate,
      publicHoliday: baseHourlyRate * phRate,
    },
    loadingRates: {
      evening: baseHourlyRate * (afternoonMult - 1),
      night: baseHourlyRate * (nightMult - 1),
    },
    overtime: [],
  });
  ORDINARY_PAY_UNITS.set(result, ordinaryPayUnits);
  return result;
}

/**
 * Cross-shift overtime variant. Per cl 19.1:
 *  - Mon–Sat: first 2 hours OT @ 150%, beyond 2h @ 200%.
 *  - Sunday: all OT @ 200%.
 *  - Public holiday: all OT @ 250% (from minute one — no 2h cutover).
 *  - 38h ordinary per week → hours over 38 are OT.
 *  - 8h ordinary per shift → hours over 8 are OT (configurable via
 *    ordinaryHoursLimitPerShift; default 8).
 *  - Cl 19.1(c) substitution: weekend / shift loadings do NOT stack
 *    on top of OT. We compute pay segment-by-segment and choose the
 *    higher of (OT rate) vs (penalty rate) for the dollar value.
 *  - RN L4 + L5: NO overtime entitlement. Falls through to base * hours.
 */
function configuredDailyOrdinaryLimit(
  limit: number | undefined,
  defaultDailyLimit: number,
): number {
  return typeof limit === 'number' && Number.isFinite(limit) && limit >= 0
    ? limit
    : defaultDailyLimit;
}

/**
 * Medea Nurses EA cl 22.9 "Part-Time Shift Workers – Work Outside Rostered
 * Shifts" — daily OT clamp at 8h. 2026-09-17 re-derivation (F9): cl 22.9's
 * own opening words scope it to PART-TIME shift workers, not "ALL EA
 * nurses" as previously coded. See `nursesDailyOvertimeThresholdHours` for
 * the scoping gate. Human clause verification pending.
 */
export const NURSES_EA_DAILY_OVERTIME_THRESHOLD_HOURS = 8;

/**
 * Medea Nurses EA cl 22.9 — permanent EA shiftworkers receive flat 200% on ALL
 * weekday OT hours (including fortnight-cap-driven OT). Human verification pending.
 */
export const NURSES_EA_SHIFTWORKER_FLAT_WEEKDAY_OT_ENABLED = true;

/**
 * 17 Sep fix (r2 audit Finding 2, P1 over-pay): both Medea Nurses EA cl 22.9
 * rules above previously gated on `ctx.isEba === true` alone. `isEba` and
 * `ebaAgreementId` are populated from ANY tenant's compiled EA rate version
 * (apps/web/src/lib/payroll/rate-context.ts) — a different provider's
 * MA000034 EA, with its own agreement id and no Medea rule blocks compiled
 * onto the version, would still have `isEba === true` and incorrectly
 * inherit Medea's 8h clamp and flat-200% weekday OT.
 *
 * Gate on an explicit agreement capability instead: does this rate version
 * actually carry any EA penalty overlay at all? A bare `isEba: true` with an
 * empty `penalties` overlay (the r2 audit Finding 2 repro shape — no Medea
 * rule blocks, no penalty bands) is not a genuinely compiled Nurses EA rate
 * version and must fall through to the base award's cl 19.1 tiered OT. We do
 * NOT check `ctx.ebaAgreementId` or any tenant/agreement literal — a future
 * MA000034 EA tenant with its own compiled penalty overlay gets this same
 * capability signal without an engine change.
 */
function nursesEaHasCompiledRuleBlockCapability(ctx: RateContext): boolean {
  return (
    ctx.isEba === true &&
    (ctx.baseAwardCode ?? ctx.awardCode) === 'MA000034' &&
    (ctx.penalties?.length ?? 0) > 0
  );
}

/**
 * MA000034 cl 19.1 and Medea Nurses EA cl 22.9 — daily overtime arises beyond
 * full-time daily hours (8h). Payroll profiles may carry SCHADS-shaped 10h
 * roster caps that must not suppress daily OT for part-time shiftworkers.
 */
function nursesDailyOvertimeThresholdHours(
  input: ShiftPayWithOvertimeInput,
  defaultDailyLimit: number,
  ctx?: RateContext,
): number {
  const configured = configuredDailyOrdinaryLimit(
    input.ordinaryHoursLimitPerShift,
    defaultDailyLimit,
  );
  if (!ctx || !nursesEaHasCompiledRuleBlockCapability(ctx)) return configured;
  // 2026-09-17 re-derivation (F9) — cl 22.9's own heading scopes it to
  // PART-TIME shift workers ("The provisions of this Clause apply to
  // part-time shift workers"). A day worker falls under cl 21.4's tiered
  // rates, never cl 22.9's threshold. A FULL-TIME shift worker is governed
  // by the unnumbered "Payment For Overtime" paragraph's "outside the
  // ordinary hours of their shift" test instead — approximated here as
  // their own configured ordinary-hours limit, not an 8h clamp, since the
  // engine has no other signal for "their shift's ordinary hours".
  // docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-nurses-ea-overtime-rate-basis-fix-shape.md
  const isPartTimeShiftWorker =
    input.employeeCategory === 'shiftworker' && input.isFullTime !== true;
  if (!isPartTimeShiftWorker) return configured;
  if (configured <= NURSES_EA_DAILY_OVERTIME_THRESHOLD_HOURS) return configured;
  return NURSES_EA_DAILY_OVERTIME_THRESHOLD_HOURS;
}

function nursesResolveOrdinaryHoursForOvertime(
  input: ShiftPayWithOvertimeInput,
  totalShiftHours: number,
  dailyOvertimeThreshold: number,
): number {
  if (input.ordinaryHoursCapWindows && input.ordinaryHoursCapWindows.length > 0) {
    return medeaFortnightCapOvertimeSplit({
      hoursWorked: totalShiftHours,
      capWindows: input.ordinaryHoursCapWindows,
      dailyOrdinaryCap: dailyOvertimeThreshold,
    }).ordinaryHours;
  }
  return ordinaryHoursForShiftWithinConfiguredLimits(
    input,
    totalShiftHours,
    dailyOvertimeThreshold,
  );
}

function calculateWithOvertime(
  rawInput: ShiftPayWithOvertimeInput & NursesShiftInputExtras,
  ctx?: RateContext,
): ShiftPayBreakdown {
  // Reuse the single-shift calc for the no-overtime case; overtime layers
  // on top via the substitution rule.
  const baseBreakdown = calculate({ ...rawInput, postgraduateQualificationAllowance: null }, ctx);
  if (!ctx) return baseBreakdown;

  const stream = rawInput.classificationStream ?? 'aged_care_nurse';
  const employment: EmploymentLabel = rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [
    ...(baseBreakdown.coverageGaps ?? []),
  ];
  const resolved = resolveNurseRate(ctx, rawInput, stream, employment, coverageGaps);
  if (!resolved.recognised) return baseBreakdown;
  if (resolved.excludedFromPenalties) {
    return applyPostgraduateQualificationAllowance(baseBreakdown, rawInput, resolved.level);
  }

  const baseHourlyRate = resolved.hourlyRate;
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);
  const otTierMults = nursesResolveOvertimeTierMultipliers(ctx, employment, coverageGaps);
  const weekdayCasualFactor = nursesOrdinaryCasualFactor(employment, resolved);

  const totalShiftHours = baseBreakdown.hoursWorked;
  const dailyOvertimeThreshold = nursesDailyOvertimeThresholdHours(rawInput, 8, ctx);
  const otInput = {
    ...rawInput,
    ordinaryHoursLimitPerShift: dailyOvertimeThreshold,
  };
  const ordinaryHoursForShift = nursesResolveOrdinaryHoursForOvertime(
    otInput,
    totalShiftHours,
    dailyOvertimeThreshold,
  );
  const overtimeHours = Math.max(0, totalShiftHours - ordinaryHoursForShift);

  if (overtimeHours <= 0) {
    return applyPostgraduateQualificationAllowance(baseBreakdown, rawInput, resolved.level);
  }

  // Phase 1B (Award Engine Integrity 2026-05) — refined OT-end-of-shift
  // logic. Previously the engine used the shift's END day-type to pay all
  // OT hours; this over-paid Sat→Sun OT shifts (OT minutes in Saturday
  // were paid at Sunday OT rate). The fixed logic uses
  // `getOvertimeSegmentBreakdown` to split OT hours by their ACTUAL
  // day-type and applies per-day OT rules:
  //
  //   PH: all OT at 250%, no cutover.
  //   Sunday: all OT at 200%.
  //   Mon–Sat: chronological first 2h at 150%, beyond at 200%.
  //
  // The "first 2h cutover" is CHRONOLOGICAL — walking back from shift end
  // (where OT lives) the first 2h of OT get tier 1 regardless of which
  // day they fall in. `getOvertimeSegmentBreakdown` returns the last N
  // hours in encounter order; we apply the first-2h cutover by tracking
  // remaining tier-1 budget as we walk through the day-type buckets.
  //
  // See: award-engine-integrity-architecture-2026-05-15.md §1.2.
  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const isPublicHoliday = rawInput.isPublicHoliday ?? false;

  // Re-compute classified shift (cheap; same algorithm as calculate()).
  const classificationTimes = shiftClassificationGeometry(rawInput);
  const classifiedForOT = classifyShift({
    startTime: classificationTimes.startTime,
    endTime: classificationTimes.endTime,
    breakMinutes: classificationTimes.breakMinutes,
    timezone: tz,
    isPublicHoliday,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    shiftCharacterRules: NURSES_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker:
      rawInput.permanentNightShiftWorker === true &&
      ebaPermanentNightMultiplier(ctx, employment) !== null,
  });
  const otBreakdown = getOvertimeSegmentBreakdown(classifiedForOT, overtimeHours);
  const shiftCharacterRules = ctx.shiftCharacterRules ?? NURSES_SHIFT_RULES;

  // Resolve per-day OT multipliers.
  //
  // Phase 1J — PH OT is HARDCODED to NURSES_PH_OT_MULT (cl 19.1(a)(iii)
  // 250%), NOT read from ctx. The ctx PUBLIC_HOLIDAY band represents the
  // cl 28.2(a) ORDINARY-PH rate (200%) used by `calculate()` only. Engine
  // does not read PUBLIC_HOLIDAY for OT path to avoid the pre-Phase-1J
  // single-phMult collapse defect (MD-NURSES-A7).
  const phMultForOT = otTierMults.phMultForOT;
  const tier1Mult = nursesCasualShiftworkerTier1(ctx, rawInput, otTierMults.tier1Mult);
  const tier2Mult = otTierMults.tier2Mult;
  const sundayOTMult = otTierMults.sundayOTMult;

  // Tier-1 budget = 2h, consumed in segment order (weekday/saturday only).
  // We walk the day-type buckets and apply tier1 until exhausted.
  const otByDay = otBreakdown.hoursByDayType;
  const overtimeRule = primaryOvertimeRuleFromContext(ctx);
  const overtimeUsesLoadedRate = overtimeOnLoadedRateEnabledFromContext(ctx);
  const certEmployment = employment === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  let shiftLoadingPremiumForOvertime = 0;
  const hasShiftLoadingOvertimeContext =
    otByDay.weekday > 0 &&
    ((rawInput.conditionalPenaltyCode != null &&
      (rawInput.shiftStartTime != null || rawInput.startTime != null)) ||
      baseBreakdown.bands.nightShift > 0 ||
      baseBreakdown.bands.eveningShift > 0);
  if (overtimeUsesLoadedRate && hasShiftLoadingOvertimeContext) {
    const shiftBandFromOtSegments =
      otByDay.weekday > 0
        ? pickOvertimeShiftLoadingBand(classifiedForOT, overtimeHours, shiftCharacterRules)
        : null;
    const shiftBand =
      shiftBandFromOtSegments ??
      inferShiftBandFromConditionalPenaltyCode(rawInput.conditionalPenaltyCode);
    const resolvedPremium = resolveShiftLoadingPremiumFromContext({
      ctx,
      employment: certEmployment,
      employeeCategory: rawInput.employeeCategory ?? null,
      shiftBand,
      shiftStartTime: rawInput.shiftStartTime ?? localTimeHHMM(rawInput.startTime, tz),
      conditionalPenaltyCode: rawInput.conditionalPenaltyCode ?? null,
      dayType: 'weekday',
    });
    if (resolvedPremium === null && shiftBand !== null) {
      pushPenaltyCoverageGap(coverageGaps, shiftBand, employment);
    } else {
      shiftLoadingPremiumForOvertime = resolvedPremium ?? 0;
    }
  }
  const ebaShiftworkerFlatDailyOt =
    NURSES_EA_SHIFTWORKER_FLAT_WEEKDAY_OT_ENABLED &&
    nursesEaHasCompiledRuleBlockCapability(ctx) &&
    employment === 'PERMANENT' &&
    (rawInput.employeeCategory ?? 'dayworker') === 'shiftworker';
  const casualTenantOtBandsConfigured =
    employment === 'CASUAL' &&
    (findPenaltyLine(ctx, 'OVERTIME_FIRST_2H', employment) != null ||
      findPenaltyLine(ctx, 'OVERTIME_AFTER_2H', employment) != null);
  // Medea Nurses EA cl 22.9 — part-time shiftworker daily OT beyond 8h is paid
  // at 200%, not the cl 19.2(a)(i) first-2h 150% cutover.
  // Release (17 Sep): a shift fragment may have consumed part of the 2 h first
  // tier earlier in the same shift; the EA flat-200% shiftworker rule still
  // wins when it applies.
  let tier1Remaining = ebaShiftworkerFlatDailyOt
    ? 0
    : Math.max(0, 2 - Math.max(0, rawInput.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0));
  const selectedOvertime: EnginePricingEvidence['overtime'] = [];
  let overtimePay = 0;
  // Tier hours, carried out so the component-line trigger routes to the right
  // Xero earnings rate instead of splitting by hours position. Sunday OT is
  // 200% from the first minute and PH OT is 250%; neither follows position.
  let otHoursAt15 = 0;
  let otHoursAt20 = 0;
  let otHoursAt25 = 0;

  // PUBLIC HOLIDAY first (no cutover, no tier system)
  // cl 19.1(a)(iii) permanent PH OT = 250%; cl 19.2(a)(iii) casual = 250%
  // of casual hourly rate = 2.5 × 1.25 = 3.125× base.
  if (otByDay.publicHoliday > 0) {
    selectedOvertime.push({
      band: 'otPublicHoliday',
      day: 'publicHoliday',
      hours: otByDay.publicHoliday,
      rate: baseHourlyRate * phMultForOT,
    });
    overtimePay += otByDay.publicHoliday * baseHourlyRate * phMultForOT;
    otHoursAt25 += otByDay.publicHoliday;
  }
  // SUNDAY OT (flat 2.0×, no cutover)
  // cl 19.1(a)(ii) permanent = 200%; cl 19.2(a)(ii) casual = 200% of casual
  // hourly rate = 2.0 × 1.25 = 2.5× base.
  if (otByDay.sunday > 0) {
    selectedOvertime.push({
      band: 'otRemainder',
      day: 'sunday',
      hours: otByDay.sunday,
      rate: baseHourlyRate * sundayOTMult,
    });
    overtimePay += otByDay.sunday * baseHourlyRate * sundayOTMult;
    otHoursAt20 += otByDay.sunday;
  }
  // WEEKDAY + SATURDAY OT — share tier-1 cutover. The CHRONOLOGICAL
  // first-2h rule means tier-1 is consumed in segment order. For a shift
  // ending on Sat (Fri 14 → Sat 02 OT), the OT walks backward from Sat
  // end → 2h Saturday tier-1 first, then weekday tier-2 if any.
  // For a Sat→Sun OT (Sat 14 → Sun 02 OT), the segments are Saturday at
  // top (tier-1 → 1.5×). But getOvertimeSegmentBreakdown returns
  // aggregated hoursByDayType — the chronological order within day types
  // is lost. So we approximate by allocating tier-1 to whichever weekday/
  // sat segments exist first (saturday first if both, since the
  // walk-backward typically lands on the later day first; in practice
  // for the canonical Sat→Sun example, OT is all Sunday and tier-1 is
  // irrelevant).
  if (overtimeUsesLoadedRate && otByDay.weekday > 0) {
    const loadedFirstMult = casualTenantOtBandsConfigured
      ? tier1Mult
      : (overtimeRule?.firstTwoHoursMultiplier ?? tier1Mult);
    const loadedSecondMult = casualTenantOtBandsConfigured
      ? tier2Mult
      : (overtimeRule?.afterTwoHoursMultiplier ?? tier2Mult);
    const loadedEmploymentMult = casualTenantOtBandsConfigured ? 1.0 : weekdayCasualFactor;
    const weekdayOtPay = calculateEbaTieredOvertimePay(
      baseHourlyRate,
      otByDay.weekday,
      loadedEmploymentMult,
      {
        onLoadedRate: true,
        shiftLoadingPremium: shiftLoadingPremiumForOvertime,
        firstTwoHoursMultiplier: loadedFirstMult,
        afterTwoHoursMultiplier: loadedSecondMult,
        // 17 Sep fix: this branch previously always priced against a fresh
        // 2h tier-1 budget, ignoring tier1Remaining (which is correctly
        // seeded above from overtimeInitialTierHoursConsumedEarlierThisShift
        // and from the EA flat-200% shiftworker rule). A shift fragment
        // that had already consumed tier1 earlier this shift was re-granted
        // a full 2h at 150% instead of being priced entirely at tier 2.
        tier1HoursRemaining: tier1Remaining,
      },
    );
    const selectedLoadedBase =
      baseHourlyRate * (1 + shiftLoadingPremiumForOvertime) * loadedEmploymentMult;
    const selectedFirstHours = Math.min(tier1Remaining, otByDay.weekday);
    selectedOvertime.push(
      {
        band: 'otFirst',
        day: 'weekday',
        hours: selectedFirstHours,
        rate: selectedLoadedBase * loadedFirstMult,
      },
      {
        band: 'otRemainder',
        day: 'weekday',
        hours: otByDay.weekday - selectedFirstHours,
        rate: selectedLoadedBase * loadedSecondMult,
      },
    );
    overtimePay += weekdayOtPay;
    const exportHours = otExportTierHours(
      otByDay.weekday,
      loadedFirstMult,
      loadedSecondMult,
      tier1Remaining,
    );
    otHoursAt15 += exportHours.otHoursAt15;
    otHoursAt20 += exportHours.otHoursAt20;
    tier1Remaining = Math.max(0, tier1Remaining - exportHours.otHoursAt15);
  }

  const weekdaySat: DayType[] = [
    'saturday',
    ...(overtimeUsesLoadedRate ? [] : ['weekday' as const]),
  ];
  for (const dt of weekdaySat) {
    const hoursInDay = otByDay[dt];
    if (hoursInDay <= 0) continue;
    const tier1Take = Math.min(tier1Remaining, hoursInDay);
    const tier2Take = hoursInDay - tier1Take;
    // cl 19.1(a)(i) permanent tier-1 = 150% / tier-2 = 200%;
    // cl 19.2(a)(i) casual = 150%/200% of casual hourly rate = 1.5/2.0 × 1.25,
    // or tenant-configured totals via ctx OVERTIME_* bands (M2).
    selectedOvertime.push(
      { band: 'otFirst', day: dt, hours: tier1Take, rate: baseHourlyRate * tier1Mult },
      { band: 'otRemainder', day: dt, hours: tier2Take, rate: baseHourlyRate * tier2Mult },
    );
    overtimePay += tier1Take * baseHourlyRate * tier1Mult;
    overtimePay += tier2Take * baseHourlyRate * tier2Mult;
    otHoursAt15 += tier1Take;
    otHoursAt20 += tier2Take;
    tier1Remaining -= tier1Take;
  }
  if (nursesEaShiftworkerOvertimeApplies(ctx, rawInput)) {
    const awardMinimum = nursesUnderlyingAwardMinimum(ctx, rawInput, stream, coverageGaps);
    let remaining = overtimeHours;
    const cells: { hours: number; day: DayType }[] = [];
    for (const segment of [...classifiedForOT.segments].reverse()) {
      if (remaining <= 0) break;
      const hours = Math.min(segment.hours, remaining);
      cells.unshift({ hours, day: segment.dayType });
      remaining -= hours;
    }
    selectedOvertime.length = 0;
    overtimePay = 0;
    otHoursAt15 = 0;
    otHoursAt20 = 0;
    otHoursAt25 = 0;
    let minute = 0;
    const cutover = Math.max(
      0,
      120 - (rawInput.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0) * 60,
    );
    for (const cell of cells) {
      const end = minute + cell.hours * 60;
      const exception = rawInput.nursesOvertimeException;
      const boundaries = [minute, end, cutover, exception?.startMinute, exception?.endMinute]
        .filter((v): v is number => v !== undefined && v >= minute && v <= end)
        .sort((a, b) => a - b);
      for (let i = 1; i < boundaries.length; i++) {
        const from = boundaries[i - 1];
        const to = boundaries[i];
        if (from === undefined || to === undefined || to <= from) continue;
        const hours = (to - from) / 60;
        const reliefApplies =
          exception?.approved === true &&
          exception.reason.trim().length > 0 &&
          from >= exception.startMinute &&
          from < exception.endMinute;
        // A permanent shiftworker's unrelieved EA limb is flat double time.
        // Its first two hours are priced and exported in the remainder band;
        // only a documented relief interval can use the first-tier band.
        const firstTierApplies =
          from < cutover && (rawInput.employmentType === 'casual' || reliefApplies);
        selectedOvertime.push({
          band:
            cell.day === 'publicHoliday'
              ? 'otPublicHoliday'
              : cell.day === 'sunday' || !firstTierApplies
                ? 'otRemainder'
                : 'otFirst',
          day: cell.day,
          hours,
          rate: nursesEaOvertimeRate({
            ctx,
            input: rawInput,
            base: baseHourlyRate,
            loadedBase: resolved.includesCasualLoading === true,
            awardMinimum,
            day: cell.day,
            overtimeMinute: from,
          }),
        });
        overtimePay +=
          hours *
          nursesEaOvertimeRate({
            ctx,
            input: rawInput,
            base: baseHourlyRate,
            loadedBase: resolved.includesCasualLoading === true,
            awardMinimum,
            day: cell.day,
            overtimeMinute: from,
          });
        if (cell.day === 'publicHoliday') otHoursAt25 += hours;
        else if (cell.day === 'sunday' || !firstTierApplies) otHoursAt20 += hours;
        else otHoursAt15 += hours;
      }
      minute = end;
    }
  }
  overtimePay = round2(overtimePay);

  // For the substitution-rule logic below, the `endDay` shorthand is
  // retained — but the corrected day-type allocation is what drives
  // overtimePay above. The substitution-rule pass uses the per-day
  // breakdown to back out the ordinary-rate portion per day.
  const endDay = getDay(rawInput.endTime, tz);

  // Substitution rule (cl 19.1(c)): "Overtime rates are in substitution for
  // and not cumulative upon the shiftwork loadings and weekend penalty
  // rates." When OT overlaps a penalty band OR a shift-loading band, we pay
  // the OT rate IN PLACE OF the penalty/loading, not on top of it.
  //
  // The base-breakdown calc already paid penalties + shift loadings for the
  // OT hours (treating them as ordinary). We back out the per-day-type
  // ordinary-rate dollar amount that would have been earned for those OT
  // hours, then add the corrected overtimePay above.
  //
  // Phase 1B refinement: split the back-out by the OT day-type breakdown
  // instead of assuming all OT is in the shift's end day type.
  // Phase 1J — substitution back-out uses ORDINARY rates (the rates the
  // base calc paid for OT hours treated as ordinary), now MULTIPLICATIVE
  // for casual per the Phase 1J fix:
  //   weekday: 1.0 × casualMult (cl 11.1 ordinary — also matches additive)
  //   sat:     satMult × casualMult (cl 21.1)
  //   sun:     sunMult × casualMult (cl 21.2)
  //   PH:      phMultForSub × casualMult (cl 28.2(a) ORDINARY — 2.0 post-seed)
  //
  // `phMultForSub` reads from ctx PUBLIC_HOLIDAY band which is the
  // ORDINARY value (cl 28.2(a)) post Phase 1J data-integrity migration.
  // Distinct from `phMultForOT` (NURSES_PH_OT_MULT = 2.5) above.
  const satLine = findPenaltyLine(ctx, 'SATURDAY', employment);
  const sunLine = findPenaltyLine(ctx, 'SUNDAY', employment);
  const phSubLine = findPenaltyLine(ctx, 'PUBLIC_HOLIDAY', employment);
  const satMult = resolveRequiredPenalty(ctx, 'SATURDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const sunMult = resolveRequiredPenalty(ctx, 'SUNDAY', employment, 1.75, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const phMultForSub = resolveRequiredPenalty(ctx, 'PUBLIC_HOLIDAY', employment, 2.0, {
    lookup: penaltyLookup,
    coverageGaps,
  });

  const ordinaryOtByDay = ordinaryOvertimeBackoutHours(
    baseBreakdown.bands,
    otByDay,
    rawInput.publicHolidaySegments != null,
  );
  let adjustedBasePay = baseBreakdown.basePay;
  let adjustedPenaltyPay = baseBreakdown.penaltyPay;
  const ordinaryPayUnits = ORDINARY_PAY_UNITS.get(baseBreakdown);

  if (ordinaryOtByDay.weekday > 0) {
    // Per-unit back-out (RND-04/07): round the remaining weekday unit once.
    const direct = ordinaryPayUnits
      ? backOutRoundingUnits(adjustedBasePay, [
          {
            exact: ordinaryPayUnits.weekday,
            removal: ordinaryOtByDay.weekday * baseHourlyRate * 1.0 * weekdayCasualFactor,
          },
        ])
      : null;
    const backout = round2(ordinaryOtByDay.weekday * baseHourlyRate * 1.0 * weekdayCasualFactor);
    adjustedBasePay = direct ?? round2(Math.max(0, adjustedBasePay - backout));
  }
  const satRate =
    baseHourlyRate * satMult * nursesCasualLoadingFactor(employment, satLine, ctx, resolved);
  const sunRate =
    baseHourlyRate * sunMult * nursesCasualLoadingFactor(employment, sunLine, ctx, resolved);
  const phSubRate =
    baseHourlyRate * phMultForSub * nursesCasualLoadingFactor(employment, phSubLine, ctx);
  const directPenaltyBackout =
    ordinaryPayUnits &&
    (ordinaryOtByDay.saturday > 0 ||
      ordinaryOtByDay.sunday > 0 ||
      ordinaryOtByDay.publicHoliday > 0)
      ? backOutRoundingUnits(adjustedPenaltyPay, [
          { exact: ordinaryPayUnits.saturday, removal: ordinaryOtByDay.saturday * satRate },
          { exact: ordinaryPayUnits.sunday, removal: ordinaryOtByDay.sunday * sunRate },
          {
            exact: ordinaryPayUnits.publicHoliday,
            removal: ordinaryOtByDay.publicHoliday * phSubRate,
          },
        ])
      : null;
  if (directPenaltyBackout !== null) adjustedPenaltyPay = directPenaltyBackout;
  if (directPenaltyBackout === null && ordinaryOtByDay.saturday > 0) {
    const backout = round2(
      ordinaryOtByDay.saturday *
        baseHourlyRate *
        satMult *
        nursesCasualLoadingFactor(employment, satLine, ctx),
    );
    adjustedPenaltyPay = round2(Math.max(0, adjustedPenaltyPay - backout));
  }
  if (directPenaltyBackout === null && ordinaryOtByDay.sunday > 0) {
    const backout = round2(
      ordinaryOtByDay.sunday *
        baseHourlyRate *
        sunMult *
        nursesCasualLoadingFactor(employment, sunLine, ctx),
    );
    adjustedPenaltyPay = round2(Math.max(0, adjustedPenaltyPay - backout));
  }
  if (directPenaltyBackout === null && ordinaryOtByDay.publicHoliday > 0) {
    const backout = round2(
      ordinaryOtByDay.publicHoliday *
        baseHourlyRate *
        phMultForSub *
        nursesCasualLoadingFactor(employment, phSubLine, ctx),
    );
    adjustedPenaltyPay = round2(Math.max(0, adjustedPenaltyPay - backout));
  }

  // Restore ordinary-hour loading using calculate()'s additive whole-shift
  // premium, but never reduce the pre-fix payable loading. Separate Medea
  // OT-hour entitlement remains held for adjudication (22 September M-OT-08).
  let adjustedShiftLoadingPay = baseBreakdown.shiftLoadingPay;
  let loadingBackoutEvidence:
    | {
        initialCents: number;
        correctedBackoutCents: number;
        compatibilityBackoutCents: number;
      }
    | undefined;
  if (!isPublicHoliday && endDay !== 0 && endDay !== 6 && baseBreakdown.shiftLoadingPay > 0) {
    const loadingBackout = nursesShiftLoadingOvertimeBackoutDollars({
      // calculate() loaded every paid hour using the whole-shift envelope.
      // Remove that same additive premium on OT, without casual loading.
      otShiftBandHours: {
        eveningShift: baseBreakdown.bands.eveningShift > 0 ? overtimeHours : 0,
        nightShift: baseBreakdown.bands.nightShift > 0 ? overtimeHours : 0,
      },
      baseHourlyRate,
      ctx,
      employment,
      rawInput,
      coverageGaps,
    });
    const preFixLoadingBackout = nursesShiftLoadingOvertimeBackoutForBasis(
      {
        otShiftBandHours: getOvertimeShiftLoadingBandBreakdown(
          classifiedForOT,
          overtimeHours,
          shiftCharacterRules,
        ),
        baseHourlyRate,
        ctx,
        employment,
        rawInput,
        // Preserve the pre-fix money without adding legacy diagnostic gaps.
        coverageGaps: [],
      },
      true,
    );
    const preFixShiftLoadingPay = round2(
      Math.max(0, baseBreakdown.shiftLoadingPay - preFixLoadingBackout),
    );
    const correctedShiftLoadingPay = round2(
      Math.max(0, baseBreakdown.shiftLoadingPay - loadingBackout),
    );
    adjustedShiftLoadingPay = Math.max(preFixShiftLoadingPay, correctedShiftLoadingPay);
    loadingBackoutEvidence = {
      initialCents: moneyToCents(baseBreakdown.shiftLoadingPay),
      correctedBackoutCents: moneyToCents(loadingBackout),
      compatibilityBackoutCents: moneyToCents(preFixLoadingBackout),
    };
  }

  // Phase 1O (Award Engine Integrity 2026-05-17) — Meal-OT emission per
  // MA000034 cl 17.3(b)(i) + cl 17.3(b)(ii). First meal $16.62 when OT > 1h
  // past usual finish; second meal $14.98 additional when OT > 4h cumulative.
  // CUMULATIVE semantics (worker doing 5h OT gets $16.62 + $14.98). NOT
  // all-purpose — flat emission, casual loading does NOT compound. Carve-outs:
  // provision-of-meal (cl 17.3(b)(i)) suppresses both; rare could-return-
  // home-for-meal (cl 17.3(b)(iii)) also suppresses. See NURSES_MEAL_OT_EMISSION
  // in award-data/nurses.ts. Mirror of W4 Aged Care implementation.
  const allowanceLinesOut: ShiftPayAllowanceLine[] = [...baseBreakdown.allowanceLines];
  let allowancesPayOut = baseBreakdown.allowancesPay;
  const mealCarveOut =
    (rawInput.mealProvidedDuringOT ?? false) || (rawInput.couldReturnHomeForMeal ?? false);
  const mealCrossings = overtimeMealAllowanceCrossings({
    fragmentOvertimeHours: overtimeHours,
    priorOvertimeHoursThisShift: rawInput.overtimeHoursWorkedEarlierThisShift,
    firstMealThresholdHours: NURSES_MEAL_OT_EMISSION.firstMealThresholdHours,
    secondMealThresholdHours: NURSES_MEAL_OT_EMISSION.secondMealThresholdHours,
  });
  if (mealCrossings.emitFirstMeal && !mealCarveOut) {
    const firstMeal = resolveAllowance(
      ctx,
      NURSES_MEAL_OT_EMISSION.firstMealAllowanceCode,
      employment,
      coverageGaps,
    );
    if (firstMeal) {
      allowancesPayOut = round2(allowancesPayOut + firstMeal.amount);
      allowanceLinesOut.push({
        code: 'MEAL_ALLOWANCE',
        description: firstMeal.description ?? 'Meal allowance (overtime)',
        amount: firstMeal.amount,
        clauseRef: firstMeal.clauseRef ?? NURSES_MEAL_OT_EMISSION.clauseRefFirst,
      });
    }
    if (mealCrossings.emitSecondMeal) {
      const secondMeal = resolveAllowance(
        ctx,
        NURSES_MEAL_OT_EMISSION.secondMealAllowanceCode,
        employment,
        coverageGaps,
      );
      if (secondMeal) {
        allowancesPayOut = round2(allowancesPayOut + secondMeal.amount);
        allowanceLinesOut.push({
          code: 'MEAL_OT_ADDITIONAL_ALLOWANCE',
          description: secondMeal.description ?? 'Additional meal allowance (further OT > 4h)',
          amount: secondMeal.amount,
          clauseRef: secondMeal.clauseRef ?? NURSES_MEAL_OT_EMISSION.clauseRefSecond,
        });
      }
    }
  }

  const grossTotal = sumMoney(
    adjustedBasePay,
    adjustedPenaltyPay,
    adjustedShiftLoadingPay,
    overtimePay,
    allowancesPayOut,
  );

  const result = applyPostgraduateQualificationAllowance(
    {
      ...baseBreakdown,
      bands: subtractOvertimeFromOrdinaryBands(baseBreakdown.bands, ordinaryOtByDay),
      basePay: adjustedBasePay,
      penaltyPay: adjustedPenaltyPay,
      shiftLoadingPay: adjustedShiftLoadingPay,
      allowancesPay: allowancesPayOut,
      allowanceLines: allowanceLinesOut,
      overtimeHours: round4(overtimeHours),
      overtimePay,
      overtimeFirstHours: round4(otHoursAt15),
      overtimeRemainderHours: round4(otHoursAt20),
      overtimePublicHolidayHours: round4(otHoursAt25),
      grossTotal,
      ...coverageGapsField(coverageGaps),
    },
    rawInput,
    resolved.level,
  );
  const ordinaryEvidence = enginePricingEvidence(baseBreakdown);
  return ordinaryEvidence
    ? rememberEnginePricingEvidence(result, {
        ...ordinaryEvidence,
        overtime: selectedOvertime,
        ...(loadingBackoutEvidence ? { loadingBackout: loadingBackoutEvidence } : {}),
      })
    : result;
}

// ═══════════════════════════════════════════════════════════════════════════
// Class wrapper
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Semver of the Nurses Award (MA000034) engine. Bumped manually on every
 * math-affecting change to this file. Persisted to
 * `pay_run_items.snapshot_engine_version` via `finalise_pay_run_snapshot`,
 * so each row is byte-reproducible against the engine code that produced it.
 *
 * Enforced by `.husky/pre-commit` + CI assertion: any modification to this
 * file (other than the version line itself) requires this string to change,
 * unless the commit message contains `[engine-no-math-change]`.
 */
// Phase 1N (2026-05-17) — bumped 5.5.6 → 5.6.0 (semver MINOR). Output contract
// change per `project_allowance_code_naming_taxonomy.md`. Emission codes use
// canonical _ALLOWANCE-suffixed names (KM → KM_ALLOWANCE; MEAL → MEAL_ALLOWANCE;
// ON_CALL_WEEKDAY → ON_CALL_WEEKDAY_ALLOWANCE; etc.). Engine math UNCHANGED.
// DB lookup keys unchanged. Bidirectional handler at
// `packages/utils/src/allowance-code-canonical.ts` translates historical codes.
//
// Phase 1O (2026-05-17) — bumped 5.6.0 → 5.6.1 (semver PATCH; math change).
// Meal-OT split (cl 17.3(b)(i) + (ii)) + return-home carveout (cl 17.3(b)(iii))
// per W22 evidence pack. Emission moved from OT-unaware calculate() (where it
// fired on `hasDelayedMealAllowance` boolean — silently dropping the
// $14.98 further-meal allowance for every OT > 4h shift) to OT-aware
// calculateWithOvertime() where it triggers on the cl 17.3(b)(i)+(ii)
// OT-duration thresholds. Carve-out flags `mealProvidedDuringOT` +
// `couldReturnHomeForMeal` suppress both emissions per cl 17.3(b)(iii).
// W22 P0 wage-theft criminal regime exposure CLOSED. Mirror of W4 Aged Care
// shape per cross-award native symmetry binding (encoded independently per
// per-award isolation rule — see NURSES_MEAL_OT_EMISSION in award-data/nurses.ts).
// 5.6.2 — Phase 1 Deep-Fix PR #3 (P1-A): backward-compat weekend on-call
// path now derives Saturday vs Sunday/PH from shift date (was: prefer Sun
// rate → over-pay $6.87/event on Saturday-only on-call). PH-wins rule
// applied per cl 17.2(a)(i) (Saturday that is also a PH → Sun/PH rate).
// Two-day weekend (count=2) emits separate Sat + Sun lines. Phase 1F
// `onCallPeriods` remains the post-Madea proper on-call register path.
//
// Phase 3 PR #3 sub-phase 3G (2026-05-18) — bumped 5.6.2 → 5.6.3 (semver PATCH).
// New LEAVE_LOADING_ALLOWANCE emission per cl 22.5(a) gated on
// segmentKind === 'paid_annual_leave' AND non-casual employmentType. Flat
// 17.5% × ordinary leave pay; cl 22.5(b) shiftworker higher-of is applied by
// the pay-run leave-snapshot layer. All pre-3G callers default segmentKind='work' →
// byte-equal for work segments. T2 sweep + Path-A pins cover the new
// emission path.
//
// Clause ref specialist-verified 2026-05-19 (Codex Pass A retrospective
// P2-N — citation-only fix, engine math unchanged for the leave-loading
// emission). Pre-fix cited cl 33.4 which does not exist as annual leave
// loading in MA000034.
//
// Phase Codex Pass A (2026-05-19) — bumped 5.6.3 → 5.7.0 (semver MINOR).
// MA000034 cl 20.1 COMMENCE+FINISH AND-trigger now honored. Pre-Phase
// finish-only shape over-paid shift loading for 10:00-18:30 (commences
// before noon — fails cl 20.1(a) commence predicate) and 17:00-01:00
// (commences before 18:00 — fails cl 20.1(b) commence predicate).
// Direction: OVER-pay corrected DOWN. Engine math CHANGED for affected
// shift patterns; byte-equal preserved for shifts that satisfy both
// commence AND finish predicates (the post-Phase shape is a strict
// SUBSET of the pre-Phase classification surface). Backward-compat tests
// using 12:00+ commence shifts unchanged.
// Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
//
// Phase Codex Pass A PR #B (2026-05-19) — bumped 5.7.0 → 5.7.1 (semver PATCH).
// [engine-math-changed] tag — `calculateRecall` math materially changed
// per FWC cl 19.1 + cl 19.2(a) primary-source verification. Pre-fix
// `NursesEngine.calculateRecall` collapsed all paid hours to a single
// flat 1.5× tier-1 multiplier; post-fix applies banded OT for Mon-Sat
// (first 2h @ 150% / casual 187.5%, after 2h @ 200% / casual 250% per
// cl 19.2(a)(i)), FLAT 200% / casual 250% for Sunday (cl 19.2(a)(ii)),
// FLAT 250% / casual 312.5% for PH (cl 19.2(a)(iii)). Saturday joins
// the Mon-Fri tier curve (cl 19.1 wording) — distinct from AC
// cl 25.1(a)(i)(B) Saturday-flat. Casual loading is MULTIPLICATIVE
// per cl 19.2(a) "% of casual hourly rate". `calculate` +
// `calculateWithOvertime` (timesheet path) are UNCHANGED — only recall
// pay is affected. Artifact:
// docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
//
// Phase 4 Medea EA on-call bridge (2026-06-06) — bumped 5.7.1 → 5.7.2
// (semver PATCH). `onCallPeriods[]` now accepts an optional per-period
// `isPublicHoliday` flag, allowing rostered multi-day on-call spans to apply
// the already-verified Nurses cl 17.2(a) Sunday/PH rate only to affected
// local-date periods. This is caller-evidence wiring for the existing PH-wins
// branch, not a new clause interpretation. Regression:
// packages/utils/src/__tests__/nurses-engine.test.ts.
//
// Phase 4 Medea EA permanent-night bridge (2026-06-09) — bumped 5.7.2 →
// 5.7.3. Base MA000034 still does not infer a permanent-night loading; EBA
// contexts may activate it only when the staff profile sets
// permanentNightShiftWorker and the rate context has PERMANENT_NIGHT_SHIFT.
//
// EA ordinary-hours cycle bridge (2026-06-09) — bumped 5.7.3 → 5.7.4
// (semver PATCH). `calculateWithOvertime` now accepts configured ordinary
// cycle caps and returns ordinary-hour bands with OT-tail hours removed, so
// pay-run hour columns and cumulative ordinary tracking match OT substitution.
// Legacy callers without cycle inputs remain byte-equal for pay dollars.
//
// EBA Phase 2 strict-mode (2026-07-03) — bumped 5.7.5 → 5.7.6. EBA
// required penalty bands now fail loud with coverageGaps instead of falling
// back to modern-award constants. Non-EBA output is byte-equal by the Phase
// 2 golden.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.7.6 → 5.7.7. Required EBA
// allowance rows now fail loud through coverageGaps when a triggered Nurses
// allowance lookup is missing. Non-EBA output is byte-equal.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.7.7 → 5.7.8. Recall penalty
// multipliers now fail loud through coverageGaps when a required EBA penalty
// row is missing.
//
// Medea payroll readiness (2026-07-06) — bumped 5.7.9 → 5.7.10. Shared
// OT band substitution now removes weekday loading overlays and ordinary-hour
// replay no longer double-counts loading overlays.
// E4 Nurses EA cl 22.1 (2026-07-15) — bumped 5.7.11 → 5.7.12. Afternoon/night
// shift-character windows are now EA-supplied via RateContext.shiftCharacterRules
// (data-driven); output is identical when absent (non-EA / unconfigured).
// Medea missed-meal-break cause gate (2026-07-30) — bumped 5.7.12 → 5.7.13.
// Approved cause evidence can now route explicit-zero MA000034 events into the
// existing 30-minute INTERRUPTED_MEAL_BREAK premium window.
// Medea Nurses EA postgraduate qualification allowance (2026-08-13) —
// bumped 5.7.13 → 5.8.0. Eligible 4%/6.5%/7.5% staff assignments now emit a
// separately mapped component for ordinary hours and are included in
// overtime and annual-leave calculations per agreement cls 18.4-18.6.
// Medea pay-run G2 (2026-09-16) — bumped 5.8.1 → 5.8.2. Daily OT threshold
// is capped at 8h for MA000034 / Nurses EA cl 22.9 even when a profile carries
// a SCHADS-shaped 10h cap; EBA shiftworkers receive flat 200% weekday daily OT.
// P45 (2026-09-16) — 5.8.3 → 5.8.4: EA-only 8h daily OT clamp, P27 M4 end-band
// shift-loading backout, BASE_PLUS casual loading contract on penalty ctx lines.
// Release merge (2026-09-17) — 5.8.6 (wave 4) + main 5.8.3 (classification
// geometry, meal crossings, earlier-fragment tier consumption) → 5.8.7.
// r2 audit Finding 1 + Finding 2 (2026-09-17) — bumped 5.8.7 → 5.8.8. (1)
// The loaded-rate weekday-OT branch now honours `tier1Remaining` instead of
// re-granting a fresh 2h tier-1 budget to a fragment that already consumed
// it earlier the same shift (under-pay fix, e.g. $212.85 → $255.42 for a
// tier-1-exhausted 3h fragment). (2) The Medea Nurses EA cl 22.9 8h daily OT
// clamp and flat-200% shiftworker weekday OT now gate on an actual compiled
// EA penalty overlay (`nursesEaHasCompiledRuleBlockCapability`), not the
// bare `ctx.isEba` flag — a different MA000034 EA tenant with no compiled
// Medea rule blocks no longer inherits Medea's clamp/flat rate (over-pay
// fix). Byte-equal for every context that already carried a compiled EA
// penalty overlay.
// Nurses EA overtime rate-basis re-derivation (2026-09-17) — 5.8.8 → 5.8.9.
// (1) `overtimeOnLoadedRateForBlock` no longer defaults MA000034 on — the
// unnumbered "Payment For Overtime" paragraph names an unqualified "double
// time" and cl 22.22 (the withdrawn authority) is the unrelieved-shift-worker
// provision, not a rate-basis clause; withdraws the whole M3 +$44.78 Hannah
// Mighall uplift and G2 Kelsey Beale's $18.66 → $13.10 (see eba-block-
// compiler.ts). (2) `nursesDailyOvertimeThresholdHours`'s 8h clamp (cl 22.9)
// is now scoped to PART-TIME shift workers only, per the clause's own
// heading — a full-time shift worker at the same tenant cap is no longer
// clamped. (3) Casual overtime multipliers are now a cl 21.6 greater-of
// against the MA000034 award-derived figure, not whatever an EA declares.
// docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-nurses-ea-overtime-rate-basis-fix-shape.md
// Round 2 retains the user-pinned correction version (5.8.10, main).
// MZ-004 D5 (2026-10-05) — 5.8.10 → 5.8.20. DST-start ordinary top-up line for
// permanent work segments that cross a forward transition; all else byte-equal.
// 5.8.20 because main's f1 correction took 5.8.10; no local worktree or branch
// claims 5.8.20 (checked 6 Oct 2026).
// Lane A: default casual NEA shiftworker first-tier floor; higher tiers retained.
// S0-05 Round 2: both-clock calendar spans feed the shared classifier.
// Half-cent rounding (2026-10-08) — 5.8.23 → 5.8.24. round2 now uses the shared
// exact half-up roundMoney (money-rounding.ts); exact half-cent amounts round
// up instead of down. No clause, rate or multiplier change.
// Overtime back-out rounds once (2026-10-08, Astra RND-04) — 5.8.24 → 5.8.25. The
// remaining ordinary/penalty dollars of hours that became overtime are priced
// once per engine rounding unit (backOutRoundingUnits) instead of
// round(round(total) − back-out), which drifted ±1c. No clause, rate or
// multiplier change.
// Overtime back-out per rounding unit (2026-10-08, Astra RND-07) — 5.8.25 → 5.8.26.
// calculate() records its exact per-band ordinary pay units; the back-out
// replaces only the units that lose hours (backOutRoundingUnits), so other
// separately rounded money in the component (e.g. a missed-meal penalty) is
// never re-rounded away. No clause, rate or multiplier change.
// ENG1: explicit casual shiftworker OT totals participate in flat daily OT.
// CAP Round 10: retain exact selected pricing evidence for frozen ledger replay.
// ORACLE Round 7: combine ENG1 loaded weekend rates with integration evidence.
// CASUALCONV round 3: preserve the loaded flat rate for excluded RN L4/L5.
// BANDFIX: apply one casual loading after normalisation when WEEKDAY_DAY is marked loaded.
// Merge of both independently changed engines: one successor beyond 5.8.33 and 5.8.29.
// 5.8.36: AE530670 EN Schedule 1 aliases use their independent national minimum.
export const ENGINE_VERSION = '5.8.36';

export class NursesEngine implements AwardEngine {
  readonly awardCode: AwardCode = 'MA000034';
  readonly engineVersion: string = ENGINE_VERSION;
  readonly engineRevisionSha: string = process.env.STATURA_ENGINE_REVISION ?? 'dev';
  buildEvaluationTrace(
    input: ShiftPayInput | ShiftPayWithOvertimeInput,
    ctx?: RateContext,
  ): EvaluationTrace {
    if (!ctx) return unavailableEvaluationTrace('NURSES_RATE_CONTEXT_REQUIRED');
    const typed = input as ShiftPayWithOvertimeInput & NursesShiftInputExtras;
    const breakdown =
      'ordinaryHoursLimitPerShift' in input
        ? calculateWithOvertime(typed, ctx)
        : calculate(typed, ctx);
    if (breakdown.baseHourlyRate <= 0 || typed.hasSleepover) {
      return unavailableEvaluationTrace(
        typed.hasSleepover
          ? 'NURSES_SLEEPOVER_INTERVALS_NOT_CLASSIFIED'
          : 'NURSES_RATE_UNRECOGNISED',
      );
    }
    const timezone = typed.timezone;
    if (!timezone) return unavailableEvaluationTrace('NURSES_TENANT_TIMEZONE_REQUIRED');
    const geometry = shiftClassificationGeometry(typed);
    if (
      geometry.startTime.getTime() !== input.startTime.getTime() ||
      geometry.endTime.getTime() !== input.endTime.getTime()
    ) {
      return unavailableEvaluationTrace('NURSES_FRAGMENT_ENVELOPE_CANNOT_FORM_ITEM_TRACE');
    }
    const employment: EmploymentLabel = typed.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
    const base = breakdown.baseHourlyRate;
    const eaShiftworker = nursesEaShiftworkerOvertimeApplies(ctx, typed);
    const traceResolved = resolveNurseRate(
      ctx,
      typed,
      typed.classificationStream ?? 'aged_care_nurse',
      employment,
    );
    const awardMinimum = eaShiftworker
      ? nursesUnderlyingAwardMinimum(
          ctx,
          typed,
          typed.classificationStream ?? 'aged_care_nurse',
          [],
        )
      : 0;
    if (eaShiftworker && employment === 'CASUAL' && awardMinimum <= 0)
      return unavailableEvaluationTrace('NURSES_INDEPENDENT_AWARD_MINIMUM_REQUIRED');
    const regularBand =
      breakdown.bands.publicHoliday > 0
        ? 'PUBLIC_HOLIDAY'
        : breakdown.bands.sundayOrdinary > 0
          ? 'SUNDAY'
          : breakdown.bands.saturdayOrdinary > 0
            ? 'SATURDAY'
            : 'WEEKDAY_DAY';
    const penaltyMultiplier = (band: string, fallback: number) =>
      findPenalty(ctx.penalties, band, employment) ?? fallback;
    const ordinaryCasual = nursesOrdinaryCasualFactor(employment, traceResolved);
    const regularMultiplier =
      regularBand === 'PUBLIC_HOLIDAY'
        ? penaltyMultiplier('PUBLIC_HOLIDAY', 2) *
          nursesCasualLoadingFactor(
            employment,
            findPenaltyLine(ctx, 'PUBLIC_HOLIDAY', employment),
            ctx,
          )
        : regularBand === 'SUNDAY'
          ? penaltyMultiplier('SUNDAY', 1.75) *
            nursesCasualLoadingFactor(
              employment,
              findPenaltyLine(ctx, 'SUNDAY', employment),
              ctx,
              traceResolved,
            )
          : regularBand === 'SATURDAY'
            ? penaltyMultiplier('SATURDAY', 1.5) *
              nursesCasualLoadingFactor(
                employment,
                findPenaltyLine(ctx, 'SATURDAY', employment),
                ctx,
                traceResolved,
              )
            : ordinaryCasual;
    const loading =
      breakdown.bands.eveningShift > 0
        ? 'WEEKDAY_AFTERNOON'
        : breakdown.bands.nightShift > 0
          ? 'WEEKDAY_NIGHT'
          : null;
    if (breakdown.overtimeHours > 0 && loading)
      return unavailableEvaluationTrace('NURSES_LOADED_OVERTIME_PER_INTERVAL_RATE_NOT_RETAINED');
    const loadingMultiplier = loading
      ? penaltyMultiplier(loading, loading === 'WEEKDAY_AFTERNOON' ? 1.125 : 1.15)
      : 1;
    const traceTiers = nursesResolveOvertimeTierMultipliers(ctx, employment, []);
    const traceTier1 = nursesCasualShiftworkerTier1(ctx, typed, traceTiers.tier1Mult);
    const traceFirstMs = Math.round(breakdown.overtimeFirstHours * 3_600_000);
    const totalPaidMs = Math.round(breakdown.hoursWorked * 3_600_000);
    const otStartMs = totalPaidMs - Math.round(breakdown.overtimeHours * 3_600_000);
    const price = (
      at: Date,
      allocation: 'ORDINARY' | 'OVERTIME',
      paidBefore: number,
    ): TracePrice => {
      let multiplier = regularMultiplier;
      let band = loading ?? regularBand;
      let component = loading ? 'ORDINARY_PLUS_SHIFT_LOADING' : 'ORDINARY';
      if (allocation === 'OVERTIME') {
        const otOrdinal = paidBefore - otStartMs;
        const actualDay =
          typed.publicHolidaySegments || eaShiftworker
            ? classified.segments.find((cell) => at >= cell.startTime && at < cell.endTime)?.dayType
            : undefined;
        const overtimeBand =
          actualDay === undefined
            ? regularBand
            : actualDay === 'publicHoliday'
              ? 'PUBLIC_HOLIDAY'
              : actualDay === 'sunday'
                ? 'SUNDAY'
                : actualDay === 'saturday'
                  ? 'SATURDAY'
                  : 'WEEKDAY_DAY';
        if (overtimeBand === 'PUBLIC_HOLIDAY') multiplier = NURSES_PH_OT_MULT;
        else if (overtimeBand === 'SUNDAY') multiplier = 2;
        else multiplier = otOrdinal < traceFirstMs ? traceTier1 : traceTiers.tier2Mult;
        if (overtimeBand === 'PUBLIC_HOLIDAY' || overtimeBand === 'SUNDAY')
          multiplier *= nursesCasualLoadingFactor(employment, undefined, ctx);
        band =
          overtimeBand === 'PUBLIC_HOLIDAY'
            ? 'PUBLIC_HOLIDAY_OVERTIME'
            : overtimeBand === 'SUNDAY'
              ? 'SUNDAY_OVERTIME'
              : otOrdinal < traceFirstMs
                ? 'OVERTIME_TIER_1'
                : 'OVERTIME_TIER_2';
        component = 'OVERTIME';
      } else if (loading) multiplier += loadingMultiplier - 1;
      const dollarRate =
        allocation === 'OVERTIME' && eaShiftworker
          ? nursesEaOvertimeRate({
              ctx,
              input: typed,
              base,
              loadedBase: traceResolved.includesCasualLoading === true,
              awardMinimum,
              day:
                band === 'PUBLIC_HOLIDAY_OVERTIME'
                  ? 'publicHoliday'
                  : band === 'SUNDAY_OVERTIME'
                    ? 'sunday'
                    : 'weekday',
              overtimeMinute: (paidBefore - otStartMs) / 60_000,
            })
          : base * multiplier;
      const rateCents6 = Math.round(dollarRate * 100 * 1_000_000);
      // The engine rounds weekday base, the (single) weekend/PH penalty band,
      // the shift loading and overtime as separate components (Astra RND-02):
      // the trace reconciles on those same groups.
      const regularGroup = regularBand === 'WEEKDAY_DAY' ? 'BASE' : 'PENALTY';
      const regularCents6 = Math.round(base * regularMultiplier * 100 * 1_000_000);
      const components =
        component === 'OVERTIME'
          ? [{ group: 'OVERTIME', rateCents6 }]
          : component === 'ORDINARY_PLUS_SHIFT_LOADING' && rateCents6 !== regularCents6
            ? [
                { group: regularGroup, rateCents6: regularCents6 },
                { group: 'SHIFT_LOADING', rateCents6: rateCents6 - regularCents6 },
              ]
            : [{ group: regularGroup, rateCents6 }];
      return {
        band,
        component,
        multiplierBasisPoints: Math.round((dollarRate / base) * 10_000),
        rateCents6,
        components,
      };
    };
    const classified = classifyShift({
      startTime: geometry.startTime,
      endTime: geometry.endTime,
      breakMinutes: 0,
      timezone,
      isPublicHoliday: typed.isPublicHoliday ?? false,
      publicHolidaySegments: typed.publicHolidaySegments,
      shiftCharacterRules: ctx.shiftCharacterRules ?? NURSES_SHIFT_RULES,
      dominantDayRule: 'STRICT_MAJORITY',
      permanentNightShiftWorker:
        typed.permanentNightShiftWorker === true &&
        ebaPermanentNightMultiplier(ctx, employment) !== null,
    });
    return buildNativeEvaluationTrace({
      input,
      breakdown,
      segments: classified.segments,
      paidRateBoundariesMs: eaShiftworker
        ? [
            otStartMs +
              Math.max(
                0,
                120 - (typed.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0) * 60,
              ) *
                60000,
            ...(typed.nursesOvertimeException
              ? [
                  otStartMs + typed.nursesOvertimeException.startMinute * 60000,
                  otStartMs + typed.nursesOvertimeException.endMinute * 60000,
                ]
              : []),
          ]
        : undefined,
      overtimeMilliseconds: Math.round(breakdown.overtimeHours * 3_600_000),
      price,
    });
  }

  calculate(input: ShiftPayInput, ctx?: RateContext): ShiftPayBreakdown {
    return applyDstStartOrdinaryTopUp(
      calculate(input as ShiftPayInput & NursesShiftInputExtras, ctx),
      input,
      this.awardCode,
    );
  }

  calculateWithOvertime(input: ShiftPayWithOvertimeInput, ctx?: RateContext): ShiftPayBreakdown {
    return applyDstStartOrdinaryTopUp(
      calculateWithOvertime(input as ShiftPayWithOvertimeInput & NursesShiftInputExtras, ctx),
      input,
      this.awardCode,
    );
  }

  calculateRecall(
    input: RecallPayInput,
    ctx: RateContext,
    baseHourlyRate: number,
    employmentType: 'PERMANENT' | 'CASUAL',
  ): RecallPayBreakdown {
    return calculateRecallPay(input, NURSES_RECALL_RULES, ctx, baseHourlyRate, employmentType);
  }
}

export const nursesEngine = new NursesEngine();
